import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { Upload, Edit3, Check, X, ChevronDown, ChevronUp, Activity, RefreshCw, Save, BarChart2, AlertTriangle, Download, RotateCcw } from 'lucide-react';
import { periodGrid } from '../utils/periods';

// ── Driver PDF columns (Date is always included) ──────────────────────────────
// kind: time | hrs | num | pct | dec.  sum:true → show a column total.
const PDF_COLS = [
  { key: 'start',                label: 'Start (corrected)', kind: 'time' },
  { key: 'finish',               label: 'Finish',            kind: 'time' },
  { key: 'correctedHours',       label: 'Corrected Hrs',     kind: 'hrs', sum: true },
  { key: 'pphRaw',               label: 'P/Hr Raw',          kind: 'dec' },  // onboard ÷ raw hrs (weighted total)
  { key: 'pphOnb',               label: 'P/Hr Onb',          kind: 'dec' },  // Sheet1 parcels ÷ corrected hrs (weighted total)
  { key: 'rawHours',             label: 'Raw Hrs',           kind: 'hrs', sum: true },
  { key: 'avgOnRoadHours',       label: 'On-Road Hrs',       kind: 'hrs', sum: true },
  { key: 'onboardItems',         label: 'Onboard',           kind: 'num', sum: true },
  { key: 'deliveredItems',       label: 'Delivered',         kind: 'num', sum: true },
  { key: 'deliveredPct',         label: 'Del %',             kind: 'pct' },
  { key: 'cardedItems',          label: 'Carded',            kind: 'num', sum: true },
  { key: 'cardedPct',            label: 'Crd %',             kind: 'pct' },
  { key: 'undeliveredItems',     label: 'Undelivered',       kind: 'num', sum: true },
  { key: 'missingInTransit',     label: 'Missing In Transit',kind: 'num', sum: true },
  { key: 'pickupItems',          label: 'Pickup',            kind: 'num', sum: true },
  { key: 'avgStopsPerOnRoadHour',label: 'Stops / Hr',        kind: 'dec' },
  { key: 'avgArticlesPerStop',   label: 'Articles / Stop',   kind: 'dec' },
  { key: 'avgStopsPerRun',       label: 'Stops / Run',       kind: 'dec' },
];
const DEFAULT_PDF_COLS = ['start', 'finish', 'correctedHours', 'pphRaw', 'pphOnb', 'avgOnRoadHours', 'onboardItems', 'deliveredItems', 'deliveredPct'];

// ── Admin roster ──────────────────────────────────────────────────────────────
// The three people whose portal shift is back-office time rather than a delivery
// round. They clock on in the same portal as the drivers, so without this list their
// hours sit in the driver denominators and quietly drag every parcels-per-hour figure
// down. Jayda also takes a relief run most days — her on-road hours are netted off,
// so a day she drives counts as driver time and only the balance counts as admin.
const ADMIN_LOGINS = { BOUVENGP2: 'Paul', BOUVENGJ: 'Jayda', ASTORGACV: 'Val' };

// Depot bags are a separate job done inside the same shift — running the bags out to
// the post offices. Nobody clocks it separately, so it can only ever be an allowance:
// a fixed slice per run plus a bit per bag. These are the starting rates; Paul sets
// the real ones on the page and they stick (per browser).
const DEPOT_ALLOWANCE_KEY = 'hp-depot-bag-allowance';
const DEPOT_ALLOWANCE_DEFAULT = { perRun: 15, perBag: 1.5 };

// ── Period helpers (same anchor as all other pages) ───────────────────────────
function formatMed(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}
// Walks out from TODAY's period (periodGrid), not from the anchor: a fixed
// -24…+6 window around 16 May 2026 ran out on 31 Oct 2026, hiding the current period.
function generatePeriods() {
  return periodGrid({ back: 24, forward: 6 });
}
const PERIODS = generatePeriods();
function findCurrentPeriod() {
  return PERIODS.find(p => p.current) || PERIODS[0];
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function parseTimeToDecimal(t) {
  if (!t) return null;
  const p = t.split(':');
  if (p.length < 2) return null;
  return parseInt(p[0]) + parseInt(p[1]) / 60 + (parseInt(p[2]) || 0) / 3600;
}
function computeHours(start, end) {
  const s = parseTimeToDecimal(start);
  const e = parseTimeToDecimal(end);
  if (s === null || e === null) return null;
  let h = e - s;
  if (h < 0) h += 24;
  return Math.min(Math.round(h * 100) / 100, 16);
}
// Normalise an AP date cell to ISO YYYY-MM-DD. Accepts ISO (old export format)
// or DD/MM/YYYY (current export puts the real date in Max./Min. Event Local Date;
// the "Event Date" column is now just "*"). Returns '' if unrecognised.
function normalizeDate(s) {
  const t = (s || '').trim();
  if (!t) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;                 // already ISO
  const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);        // DD/MM/YYYY
  if (m) {
    const [, d, mo, y] = m;
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return '';
}
function pct(n) { return n != null ? Number(n).toFixed(1) + '%' : '—'; }
function num(n)  { return n != null && n !== '' ? Number(n).toLocaleString() : '—'; }
function hrs(n)  { return n != null ? Number(n).toFixed(2) + ' h' : '—'; }
function dp(n, d=1) { return n != null && !isNaN(n) ? Number(n).toFixed(d) : '—'; }

// ── CSV parser ────────────────────────────────────────────────────────────────
function parseCSV(text, usualStartMap) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  const headers = lines[0].split('\t').map(h => h.trim());
  const col = name => headers.indexOf(name);

  const iLogin = col('User Logon Id');
  const iDate  = col('Event Date');
  const iMaxD  = col('Max. Event Local Date');
  const iMinD  = col('Min. Event Local Date');
  const iState = col('Wcc State');
  const iTerr  = col('Wcc Territory');
  const iDepot = col('Wcc Name');
  const iRole  = col('User Role');
  const iSI    = col('Start Indoor');
  const iEI    = col('End Indoor');
  const iSO    = col('Start Outdoor');
  const iEO    = col('End Outdoor');
  const iOI    = col('Onboard Items');
  const iOC    = col('Onboard Consignments');
  const iDelP  = col('Delivered %');
  const iCrdP  = col('Carded %');
  const iUndP  = col('Undel %');
  const iOnbP  = col('Onb Only %');
  const iDI    = col('Delivered Items');
  const iDC    = col('Delivered Consignments');
  const iCI    = col('Carded Items');
  const iUI    = col('Undelivered Items');
  const iUC    = col('Undelivered Consignments');
  const iMIT   = col('Missing In Transit Items');
  const iPU    = col('Pickup Items');
  const iAvgOR = col('Avg. On Road Hours');
  const iPreC  = col('Precarded Items');
  const iAvgS  = col('Avg. Stops Per On Road Hour');
  const iAvgA  = col('Avg. Articles Per Stop');
  const iAvgR  = col('Avg. Stops Per Run');

  const rows = [];
  let corrected = 0;

  lines.slice(1).forEach((line, idx) => {
    const v = line.split('\t');
    const login = (v[iLogin] || '').trim().toUpperCase();
    // Event Date is now "*" in the current AP export; the real date lives in
    // Max./Min. Event Local Date (DD/MM/YYYY). Try each, oldest export first.
    const date = normalizeDate(iDate >= 0 ? v[iDate] : '')
              || normalizeDate(iMaxD >= 0 ? v[iMaxD] : '')
              || normalizeDate(iMinD >= 0 ? v[iMinD] : '');
    if (!login || login === 'TOTAL' || !date) return;

    const csvStart = iSI >= 0 ? (v[iSI] || '').trim() : '';
    const endIn    = iEI >= 0 ? (v[iEI] || '').trim() : '';
    const startOut = iSO >= 0 ? (v[iSO] || '').trim() : '';
    const endOut   = iEO >= 0 ? (v[iEO] || '').trim() : '';

    const usualStart = usualStartMap[login] || '';
    const effectiveStart = usualStart || csvStart;
    const finishTime = endOut || endIn;
    const correctedHours = computeHours(effectiveStart, finishTime); // legal shift start → finish
    const rawHours       = computeHours(csvStart, finishTime);       // CSV scan start → finish (AP's view)
    if (usualStart) corrected++;

    const parsePct = s => { const n = parseFloat((s || '').replace('%','')); return isNaN(n) ? null : n; };
    const parseNum = s => { const n = parseInt((s || '').replace(/,/g,'')); return isNaN(n) ? null : n; };

    rows.push({
      id: `${login}|${date}|${idx}`,
      login,
      date,
      state:     iState >= 0 ? (v[iState] || '').trim() : '',
      territory: iTerr  >= 0 ? (v[iTerr]  || '').trim() : '',
      depot:     iDepot >= 0 ? (v[iDepot] || '').trim() : '',
      role:      iRole  >= 0 ? (v[iRole]  || '').trim() : '',
      startIndoorCSV:       csvStart,
      startIndoorUsual:     usualStart,
      startIndoorOverride:  '',
      startIndoorEffective: effectiveStart,
      endIndoor:   endIn,
      startOutdoor: startOut,
      endOutdoor:  endOut,
      correctedHours,
      rawHours,
      onboardItems:          parseNum(v[iOI]),
      onboardConsignments:   parseNum(v[iOC]),
      deliveredPct:          parsePct(v[iDelP]),
      cardedPct:             parsePct(v[iCrdP]),
      undelPct:              parsePct(v[iUndP]),
      onbOnlyPct:            parsePct(v[iOnbP]),
      deliveredItems:        parseNum(v[iDI]),
      deliveredConsignments: parseNum(v[iDC]),
      cardedItems:           parseNum(v[iCI]),
      undeliveredItems:      parseNum(v[iUI]),
      undeliveredConsignments: parseNum(v[iUC]),
      missingInTransit:      parseNum(v[iMIT]),
      pickupItems:           parseNum(v[iPU]),
      avgOnRoadHours:        parseFloat(v[iAvgOR]) || null,
      precardedItems:        parseNum(v[iPreC]),
      avgStopsPerOnRoadHour: parseFloat(v[iAvgS]) || null,
      avgArticlesPerStop:    parseFloat(v[iAvgA]) || null,
      avgStopsPerRun:        parseFloat(v[iAvgR]) || null,
    });
  });

  return { rows, corrected };
}

// ── Scorecard helpers ─────────────────────────────────────────────────────────
function buildScorecard(login, rows) {
  const dRows = rows.filter(r => r.login === login);
  const days = dRows.length;
  const totalOnboard   = dRows.reduce((s, r) => s + (r.onboardItems || 0), 0);
  const totalDelivered = dRows.reduce((s, r) => s + (r.deliveredItems || 0), 0);
  const totalCarded    = dRows.reduce((s, r) => s + (r.cardedItems || 0), 0);
  const totalUndel     = dRows.reduce((s, r) => s + (r.undeliveredItems || 0), 0);
  const totalMIT       = dRows.reduce((s, r) => s + (r.missingInTransit || 0), 0);
  const totalPickup    = dRows.reduce((s, r) => s + (r.pickupItems || 0), 0);
  const totalHours     = dRows.reduce((s, r) => s + (r.correctedHours || 0), 0);
  const totalRawHours  = dRows.reduce((s, r) => s + (r.rawHours       || 0), 0);
  const delPct  = totalOnboard > 0 ? (totalDelivered / totalOnboard) * 100 : null;
  const crdPct  = totalOnboard > 0 ? (totalCarded    / totalOnboard) * 100 : null;
  const pph     = totalHours    > 0 ? totalDelivered / totalHours    : null;
  const rawPph  = totalRawHours > 0 ? totalDelivered / totalRawHours : null;
  const avgHrs  = days > 0 ? totalHours / days : null;
  const avgStopsHr  = dRows.filter(r => r.avgStopsPerOnRoadHour).reduce((s,r,_,a) => s + r.avgStopsPerOnRoadHour / a.length, 0) || null;
  const avgArtStop  = dRows.filter(r => r.avgArticlesPerStop).reduce((s,r,_,a)    => s + r.avgArticlesPerStop    / a.length, 0) || null;
  const avgStopsRun = dRows.filter(r => r.avgStopsPerRun).reduce((s,r,_,a)        => s + r.avgStopsPerRun        / a.length, 0) || null;
  const depot = dRows[0]?.depot || '';
  return { login, depot, days, totalOnboard, totalDelivered, totalCarded, totalUndel, totalMIT, totalPickup, totalHours, totalRawHours, delPct, crdPct, pph, rawPph, avgHrs, avgStopsHr, avgArtStop, avgStopsRun };
}

// ── Stat chip ─────────────────────────────────────────────────────────────────
function Chip({ label, value, accent = '#1a3a6b', sub }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 18px', borderTop: `3px solid ${accent}` }}>
      <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent, margin: '6px 0 2px', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</div>}
    </div>
  );
}

// ── Hours bucket (one column of the "Where the hours go" band) ────────────────
function HourBucket({ label, hours, rate, rateLabel, accent, lines = [], first, last, children }) {
  return (
    <div style={{ borderRight: last ? 'none' : '1px solid #e5e7eb', padding: first ? '0 20px 0 0' : '0 20px' }}>
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color: accent, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
        {hours != null ? Number(hours).toFixed(1) : '—'}
        <span style={{ fontSize: 14, fontWeight: 600, color: '#9ca3af', marginLeft: 3 }}>h</span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>
        {rate != null ? Number(rate).toFixed(1) : '—'}
        <span style={{ fontSize: 11, fontWeight: 500, color: '#6b7280', marginLeft: 4 }}>{rateLabel}</span>
      </div>
      {lines.filter(Boolean).map((t, i) => (
        <div key={i} style={{ fontSize: 11, color: '#9ca3af', marginTop: i ? 1 : 6, lineHeight: 1.45 }}>{t}</div>
      ))}
      {children}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DriverActivitySummaryPage() {
  const { activityData, setActivityData, activityMeta, setActivityMeta, sheetData, addNotification } = useApp();
  const [selectedPeriod, setSelectedPeriod] = useState(findCurrentPeriod);
  const [activeTab, setActiveTab] = useState('log');
  const [filterDriver, setFilterDriver] = useState('');
  const [filterDepot, setFilterDepot]   = useState('');
  const [lateOnly, setLateOnly]         = useState(false);
  const [search, setSearch]             = useState('');
  const [editingId, setEditingId]         = useState(null);
  const [editValue, setEditValue]         = useState('');
  const [editingFinishId, setEditingFinishId] = useState(null);
  const [editFinishValue, setEditFinishValue] = useState('');
  // Analysis tab
  const [breakdownBucket, setBreakdownBucket] = useState('week'); // 'day'|'week'|'month'
  const [breakdownDriver, setBreakdownDriver] = useState('');
  const [sheet1Totals, setSheet1Totals]   = useState(null);   // { byDriver, byDate }
  const [portalTimes, setPortalTimes]     = useState(null);   // [{date,login,name,shiftStart,breakStart,breakEnd,finishTime,totalKm,totalHours}]
  const [depotRows, setDepotRows]         = useState(null);   // [{date,login,tally,route}] — depot bag runs, for the hours allowance
  const [depotAllowance, setDepotAllowance] = useState(() => {
    try { return { ...DEPOT_ALLOWANCE_DEFAULT, ...(JSON.parse(localStorage.getItem(DEPOT_ALLOWANCE_KEY) || 'null') || {}) }; }
    catch { return DEPOT_ALLOWANCE_DEFAULT; }
  });
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalFrom, setPortalFrom]       = useState('');     // Portal Times tab date-range filter (within period)
  const [portalTo, setPortalTo]           = useState('');
  const [reconcLoading, setReconcLoading] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [dragging, setDragging]         = useState(false);
  const [importing, setImporting]       = useState(false);
  const [sheetSaving, setSheetSaving]   = useState(false);
  const [sheetLoading, setSheetLoading] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef(null);

  // ── localStorage helpers ────────────────────────────────────────────────────
  const LS_KEY = 'hp-wave-activity';
  const saveLocal = (rows, meta) => {
    try { localStorage.setItem(LS_KEY, JSON.stringify({ rows, meta, ts: Date.now() })); } catch {}
  };
  const loadLocal = () => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch { return null; }
  };

  // ── Save to Wave_Activity sheet (background, best-effort) ───────────────────
  const saveToSheet = async (rows, period) => {
    if (!rows.length) return;
    setSheetSaving(true);
    try {
      const res  = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'saveWaveActivity', rows, periodStart: period.startISO, periodEnd: period.endISO }),
      });
      const data = await res.json();
      if (data.success) addNotification(`Backed up ${data.rowsSaved} records to Google Drive`, 'success');
      else              addNotification(`Drive backup failed: ${data.error}`, 'error');
    } catch (err) {
      addNotification(`Sheet save error: ${err.message}`, 'error');
    } finally {
      setSheetSaving(false);
    }
  };

  // ── Save EVERY cut-off period the rows span (background) ─────────────────────
  // wave_activity is keyed per 28-day period, so a multi-period import must persist
  // each period separately — otherwise only the auto-selected period reaches the
  // server and the rest stays browser-only (invisible to Driver Review / Hourly
  // Drivers). One summary toast instead of one-per-period.
  const saveAllPeriods = async (rows) => {
    const periods = PERIODS.filter(p => rows.some(r => r.date >= p.startISO && r.date <= p.endISO));
    if (!periods.length) return;
    setSheetSaving(true);
    let ok = 0, fail = 0;
    for (const p of periods) {
      const pr = rows.filter(r => r.date >= p.startISO && r.date <= p.endISO);
      try {
        const res = await fetch('/.netlify/functions/appsscript', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'saveWaveActivity', rows: pr, periodStart: p.startISO, periodEnd: p.endISO }),
        });
        const data = await res.json();
        if (data.success) ok += pr.length; else fail++;
      } catch { fail++; }
    }
    setSheetSaving(false);
    addNotification(`Backed up ${ok.toLocaleString()} records across ${periods.length} period${periods.length === 1 ? '' : 's'} to the server${fail ? ` · ${fail} period(s) failed` : ''}`, fail ? 'error' : 'success');
  };

  // ── Load from Wave_Activity sheet ───────────────────────────────────────────
  const loadFromSheet = async (period) => {
    setSheetLoading(true);
    try {
      const res  = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getWaveActivity', periodStart: period.startISO, periodEnd: period.endISO }),
      });
      const data = await res.json();
      if (data.success && data.rows.length) {
        const dates = [...new Set(data.rows.map(r => r.date))].sort();
        const meta = { filename: 'Loaded from sheet', records: data.rows.length, corrected: 0, dateRange: `${dates[0]} → ${dates[dates.length-1]}` };
        setActivityData(data.rows);
        setActivityMeta(meta);
        saveLocal(data.rows, meta);
        addNotification(`Loaded ${data.rows.length} records from sheet`, 'success');
      } else if (data.success) {
        addNotification('No data in sheet for this period — import a CSV to populate it', 'info');
      } else {
        addNotification(`Load failed: ${data.error}`, 'error');
      }
    } catch (err) {
      addNotification(`Load error: ${err.message}`, 'error');
    } finally {
      setSheetLoading(false);
    }
  };

  // ── On mount: restore Wave data + always auto-load Sheet1 ───────────────────
  useEffect(() => {
    // Restore Wave CSV data from localStorage
    if (activityData.length === 0) {
      const cached = loadLocal();
      if (cached?.rows?.length) {
        setActivityData(cached.rows);
        if (cached.meta) setActivityMeta({ ...cached.meta, filename: cached.meta.filename + ' (local cache)' });
      } else {
        loadFromSheet(findCurrentPeriod());
      }
    }
  }, []); // eslint-disable-line

  // ── Fetch Sheet1 totals for reconciliation ──────────────────────────────────
  const fetchSheet1Totals = useCallback(async () => {
    setReconcLoading(true);
    try {
      const res  = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getSheet1Totals', periodStart: selectedPeriod.startISO, periodEnd: selectedPeriod.endISO }),
      });
      const data = await res.json();
      if (data.success) setSheet1Totals(data);
      else addNotification('Could not load Sheet1 data: ' + data.error, 'error');
    } catch (err) {
      addNotification('Sheet1 fetch error: ' + err.message, 'error');
    } finally {
      setReconcLoading(false);
    }
  }, [selectedPeriod]); // eslint-disable-line

  // Auto-fetch Sheet1 on mount and whenever the period changes
  // (source of truth for parcel volume — used by Activity Log P/Hr columns + Analysis tab)
  useEffect(() => {
    fetchSheet1Totals();
  }, [selectedPeriod]); // eslint-disable-line

  // ── Portal Times tab: true start/finish/break from Driver_Logs (portal data) ──
  const fetchPortalTimes = useCallback(async () => {
    setPortalLoading(true);
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getPortalShiftTimes', startISO: selectedPeriod.startISO, endISO: selectedPeriod.endISO }),
      });
      const data = await res.json();
      setPortalTimes(data.success ? (data.rows || []) : []);
    } catch (err) {
      setPortalTimes([]);
    } finally {
      setPortalLoading(false);
    }
  }, [selectedPeriod]); // eslint-disable-line

  // Load portal times on mount + period change (powers the Portal P/Hr tile and
  // the Portal Times tab — needs to be available regardless of the active tab).
  useEffect(() => {
    fetchPortalTimes();
  }, [selectedPeriod]); // eslint-disable-line

  // ── Depot bag runs for the period ───────────────────────────────────────────
  // Feeds the depot-bag hours allowance. Read-only; the tallies themselves are logged
  // by the drivers in the portal and amended on Direct Entry.
  useEffect(() => {
    let alive = true;
    fetch('/.netlify/functions/depot-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getDepotsRange', from: selectedPeriod.startISO, to: selectedPeriod.endISO }),
    })
      .then(r => r.json())
      .then(d => { if (alive) setDepotRows(d.success ? (d.rows || []) : []); })
      .catch(() => { if (alive) setDepotRows([]); });
    return () => { alive = false; };
  }, [selectedPeriod]);

  // Allowance rates are a judgement call Paul tunes, so they persist per browser.
  useEffect(() => {
    try { localStorage.setItem(DEPOT_ALLOWANCE_KEY, JSON.stringify(depotAllowance)); } catch {}
  }, [depotAllowance]);

  // ── Uplift hour decisions ───────────────────────────────────────────────────
  // Where the portal and the scanner conflicted, Paul adjudicated the day in the Uplift
  // Workbench at period end. That call does NOT rewrite either record — this page still shows
  // exactly what the portal captured and what the scanner reported. It flags the day so nobody
  // reads a number here and assumes it's what the driver was paid on.
  const [hourDecisions, setHourDecisions] = useState({});   // "LOGIN|date" -> decision
  useEffect(() => {
    let alive = true;
    fetch('/.netlify/functions/gccd-uplift-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getHourDecisions', from: selectedPeriod.startISO, to: selectedPeriod.endISO }),
    })
      .then(r => r.json())
      .then((d) => {
        if (!alive || !d.success) return;
        const m = {};
        (d.decisions || []).forEach((x) => { m[`${x.driver}|${x.day}`] = x; });
        setHourDecisions(m);
      })
      .catch(() => { if (alive) setHourDecisions({}); });
    return () => { alive = false; };
  }, [selectedPeriod]);
  const decisionCount = Object.keys(hourDecisions).length;

  // ── Import ──────────────────────────────────────────────────────────────────
  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const buffer = await file.arrayBuffer();
      const decoder = new TextDecoder('utf-16le');
      let text = decoder.decode(buffer);
      if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

      const usualStartMap = {};
      (sheetData?.drivers || []).forEach(d => {
        if (d.loginName && d.indoorStart) usualStartMap[d.loginName.toUpperCase()] = d.indoorStart;
      });

      const { rows: newRows, corrected } = parseCSV(text, usualStartMap);
      if (!newRows.length) { addNotification('No valid rows found in CSV', 'error'); return; }

      // Merge: keep existing rows for dates not in this import, replace rows for dates that are
      const newDates = new Set(newRows.map(r => r.date));
      const merged = [
        ...activityData.filter(r => !newDates.has(r.date)),
        ...newRows,
      ].sort((a, b) => a.date.localeCompare(b.date) || a.login.localeCompare(b.login));

      const allDates = [...new Set(merged.map(r => r.date))].sort();
      setActivityData(merged);

      // Auto-select the period that matches the new data
      const midDate = [...newDates].sort()[Math.floor(newDates.size / 2)];
      const match = PERIODS.find(p => midDate >= p.startISO && midDate <= p.endISO);
      if (match) setSelectedPeriod(match);

      const meta = {
        filename: file.name,
        records: merged.length,
        corrected,
        dateRange: `${allDates[0]} → ${allDates[allDates.length - 1]}`,
      };
      setActivityMeta(meta);
      saveLocal(merged, meta);
      addNotification(`Imported ${newRows.length} new records · merged into ${merged.length} total · ${corrected} start times corrected`, 'success');

      // Persist EVERY cut-off period the imported data spans (not just the active one),
      // so a single multi-period import fully reaches the server and flows into Driver
      // Review / Hourly Drivers.
      saveAllPeriods(merged);
    } catch (err) {
      addNotification(`Import failed: ${err.message}`, 'error');
    } finally {
      setImporting(false);
    }
  };

  // ── Drag & drop ─────────────────────────────────────────────────────────────
  const onDragEnter = e => { e.preventDefault(); dragCounter.current++; if (e.dataTransfer.types.includes('Files')) setDragging(true); };
  const onDragLeave = () => { dragCounter.current--; if (dragCounter.current === 0) setDragging(false); };
  const onDragOver  = e => e.preventDefault();
  const onDrop      = e => { e.preventDefault(); dragCounter.current = 0; setDragging(false); handleImport(e.dataTransfer.files[0]); };

  // ── Filter data to selected period ──────────────────────────────────────────
  const periodRows = activityData.filter(r => r.date >= selectedPeriod.startISO && r.date <= selectedPeriod.endISO);

  const allDrivers = [...new Set(periodRows.map(r => r.login))].sort();
  const allDepots  = [...new Set(periodRows.map(r => r.depot))].filter(Boolean).sort();

  const effStartOf  = r => r.startIndoorOverride || r.startIndoorUsual || r.startIndoorCSV || '';
  const effFinishOf = r => r.endOverride || r.endOutdoor || r.endIndoor || '';
  // Flag finishes at/after 6:00pm — usually a scanner left logged on; the row turns red to check.
  const isLateFinish = r => { const m = /^(\d{1,2}):(\d{2})/.exec(effFinishOf(r)); return !!m && (+m[1] * 60 + +m[2]) >= 18 * 60; };
  const tMin = t => { const m = /^(\d{1,2}):(\d{2})/.exec(t || ''); return m ? +m[1] * 60 + +m[2] : null; };
  const minToHHMM = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}:00`;
  const medianOf = arr => { const s = [...arr].sort((a, b) => a - b); const n = s.length; return !n ? null : (n % 2 ? s[(n - 1) / 2] : Math.round((s[n / 2 - 1] + s[n / 2]) / 2)); };

  const filteredRows = periodRows.filter(r => {
    if (filterDriver && r.login !== filterDriver) return false;
    if (filterDepot  && r.depot  !== filterDepot)  return false;
    if (search && !r.login.includes(search.toUpperCase()) && !r.depot.toLowerCase().includes(search.toLowerCase())) return false;
    if (lateOnly && !isLateFinish(r)) return false;
    return true;
  });

  // ── Scorecards ──────────────────────────────────────────────────────────────
  const scorecards = allDrivers.map(l => buildScorecard(l, periodRows)).sort((a, b) => b.totalDelivered - a.totalDelivered);

  // ── Portal Times tab: merge portal shift times (Driver_Logs) with Sheet1 parcels ──
  // One row per driver per day. Extensible — add columns from portalTimes (KM, etc.)
  // or sheet1Totals (express/depot/doe) as the data set grows.
  const portalDaily = (portalTimes || []).map(r => {
    const cell    = sheet1Totals?.byDate?.[r.date]?.[r.login] || null;
    const parcels = cell ? Math.round(cell.parcel || 0) : 0;
    const breakMin = (r.breakStart && r.breakEnd) ? Math.max(0, Math.round(computeHours(r.breakStart, r.breakEnd) * 60)) : 0;
    const pph     = r.totalHours > 0 ? parcels / r.totalHours : null;
    return { ...r, parcels, breakMin, pph };
  }).filter(r => (!filterDriver || r.login === filterDriver)
              && (!search || r.login.toLowerCase().includes(search.toLowerCase()) || (r.name || '').toLowerCase().includes(search.toLowerCase()))
              && (!portalFrom || r.date >= portalFrom)
              && (!portalTo   || r.date <= portalTo))
    .sort((a, b) => a.date === b.date ? (a.login < b.login ? -1 : 1) : (a.date < b.date ? 1 : -1)); // newest first
  const portalDrivers = [...new Set((portalTimes || []).map(r => r.login))].sort();
  const portalTotals = portalDaily.reduce((s, r) => ({
    parcels: s.parcels + r.parcels,
    hours:   s.hours + (r.totalHours || 0),
    km:      s.km + (r.totalKm || 0),
  }), { parcels: 0, hours: 0, km: 0 });

  // ── Recover missing start/finish from the driver portal (Driver_Logs) ─────────
  // Days where the Wave CSV carries no start or no finish leave correctedHours null
  // (they show "—" and contribute zero hours). Where the driver clocked on/off in the
  // portal we can offer that exact time to fill the gap. Accepting is LOCAL ONLY (state
  // + localStorage, same store as the imported CSV) — it deliberately does NOT write
  // back to Wave_Activity, so it never leaves this browser or affects other users.
  const portalByKey = {};
  (portalTimes || []).forEach(r => { portalByKey[`${r.login}|${r.date}`] = r; });
  // The recovery available for a row, or null when nothing's missing / the portal
  // record doesn't supply the missing side.
  const portalFillFor = (r) => {
    if (r.correctedHours != null) return null;          // already has hours
    const pr = portalByKey[`${r.login}|${r.date}`];
    if (!pr) return null;
    const curStart  = effStartOf(r);
    const curFinish = effFinishOf(r);
    const newStart  = curStart  || pr.shiftStart || '';
    const newFinish = curFinish || pr.finishTime || '';
    const hours = computeHours(newStart, newFinish);
    if (hours == null) return null;                     // portal didn't fill the missing side
    return { pr, newStart, newFinish, hours, needStart: !curStart, needFinish: !curFinish };
  };
  // Accept the portal recovery for one row: set the missing side(s) as an override,
  // recompute both hour columns, persist locally. Reversible via the existing revert
  // buttons (which also clear the "portal" tag).
  const acceptPortal = (id) => {
    const updated = activityData.map(r => {
      if (r.id !== id) return r;
      const fill = portalFillFor(r);
      if (!fill) return r;
      const next = { ...r };
      if (fill.needStart && fill.pr.shiftStart) {
        next.startIndoorOverride  = fill.pr.shiftStart;
        next.startIndoorEffective = fill.pr.shiftStart;
        next.startFromPortal = true;
      }
      if (fill.needFinish && fill.pr.finishTime) {
        next.endOverride = fill.pr.finishTime;
        next.finishFromPortal = true;
      }
      const es = next.startIndoorOverride || next.startIndoorUsual || next.startIndoorCSV || '';
      const ef = next.endOverride || next.endOutdoor || next.endIndoor || '';
      next.correctedHours = computeHours(es, ef);
      next.rawHours       = computeHours(next.startIndoorCSV || '', ef);
      return next;
    });
    setActivityData(updated);
    saveLocal(updated, activityMeta);   // local only — deliberately no saveToSheet
  };
  // How many currently-visible rows have a missing start/finish the portal can fill.
  const recoverableInView = filteredRows.filter(r => portalFillFor(r)).length;

  // ── Period summary stats ─────────────────────────────────────────────────────
  const totalOnboard   = periodRows.reduce((s, r) => s + (r.onboardItems   || 0), 0);
  const totalDelivered = periodRows.reduce((s, r) => s + (r.deliveredItems  || 0), 0);
  const totalCarded    = periodRows.reduce((s, r) => s + (r.cardedItems     || 0), 0);
  const totalMIT       = periodRows.reduce((s, r) => s + (r.missingInTransit|| 0), 0);
  const totalHours     = periodRows.reduce((s, r) => s + (r.correctedHours  || 0), 0);
  const totalRawHours  = periodRows.reduce((s, r) => s + (r.rawHours       || 0), 0);

  // Sheet1 is source of truth for parcel volume
  const s1Total = sheet1Totals
    ? Math.round(Object.values(sheet1Totals.byDriver || {}).reduce((s, d) => s + (d.total || 0), 0))
    : null;
  const s1Parcels  = s1Total || totalDelivered; // fall back to Wave if Sheet1 not loaded yet

  // Sheet1 parcels (parcel + express) for a single row's driver+date — used for "onboarded" P/Hr
  const sheet1RowParcels = r => {
    const x = sheet1Totals && sheet1Totals.byDate && sheet1Totals.byDate[r.date] && sheet1Totals.byDate[r.date][r.login];
    return x ? (x.parcel || 0) + (x.express || 0) : null;
  };
  // P/Hr helpers: raw = raw-file onboard ÷ raw hours; onboarded = Sheet1 parcels ÷ corrected hours
  const rowPphRaw = r => (r.rawHours > 0 && r.onboardItems != null) ? r.onboardItems / r.rawHours : null;
  const rowPphOnb = r => { const p = sheet1RowParcels(r); return (p != null && r.correctedHours > 0) ? p / r.correctedHours : null; };

  const fleetDelPct    = totalOnboard > 0 ? (totalDelivered / totalOnboard) * 100 : null;
  const fleetCrdPct    = totalOnboard > 0 ? (totalCarded    / totalOnboard) * 100 : null;
  const fleetRawPPH    = totalRawHours > 0 ? totalOnboard / totalRawHours : null; // AP's view (Wave onboard / raw hrs)
  const fleetPPH       = totalHours   > 0 ? s1Parcels    / totalHours    : null; // Engine: Sheet1 parcels / corrected hrs
  const pphDiff        = fleetPPH != null && fleetRawPPH != null ? fleetRawPPH - fleetPPH : null;

  // ── Where the hours go ───────────────────────────────────────────────────────
  // Four buckets that between them account for the hours the depot pays for, each with
  // its own parcels-per-hour. They come off two different clocks — indoor and outdoor
  // are the AP scanner (Wave), admin is the driver portal — so each tile says which,
  // and the two clocks are never added together into a single figure.

  // A span between two times on the same shift. A day missing either end is skipped
  // rather than guessed, and anything over 12 h is a scanner left logged on, not a shift.
  const spanHours = (from, to) => {
    const a = tMin(from), b = tMin(to);
    if (a == null || b == null) return null;
    let h = (b - a) / 60;
    if (h < 0) h += 24;
    return h >= 0 && h <= 12 ? h : null;
  };
  // Indoor = corrected (legal) start → first outdoor scan: the sort and load before the
  // run. Outdoor = first outdoor scan → finish: the run itself.
  const hourSplit = periodRows.reduce((acc, r) => {
    const inH  = spanHours(effStartOf(r), r.startOutdoor);
    const outH = spanHours(r.startOutdoor, effFinishOf(r));
    if (inH  != null) { acc.indoor  += inH;  acc.indoorDays++;  } else acc.indoorSkipped++;
    if (outH != null) { acc.outdoor += outH; acc.outdoorDays++; } else acc.outdoorSkipped++;
    return acc;
  }, { indoor: 0, outdoor: 0, indoorDays: 0, outdoorDays: 0, indoorSkipped: 0, outdoorSkipped: 0 });

  // Admin hours = what the person clocked in the portal, less any hours the scanner says
  // they spent on a round that day. Jayda starts at 05:34 and doesn't pick up a run until
  // ~08:00 — that gap is the admin time; the run is driver time, already counted above.
  const waveHoursByKey = {};
  periodRows.forEach(r => {
    const k = `${r.login}|${r.date}`;
    waveHoursByKey[k] = (waveHoursByKey[k] || 0) + (r.correctedHours || 0);
  });
  const inPeriodISO = d => d >= selectedPeriod.startISO && d <= selectedPeriod.endISO;
  const adminPeople = Object.keys(ADMIN_LOGINS).map(login => {
    const rows    = (portalTimes || []).filter(r => r.login === login && inPeriodISO(r.date));
    const portalH = rows.reduce((s, r) => s + (r.totalHours || 0), 0);
    // Cap the on-road deduction at the hours actually clocked, so a scanner day that
    // outruns the portal shift can never turn admin hours negative.
    const onRoadH = rows.reduce((s, r) => s + Math.min(r.totalHours || 0, waveHoursByKey[`${login}|${r.date}`] || 0), 0);
    return { login, name: ADMIN_LOGINS[login], days: rows.length, portalH, onRoadH, adminH: Math.max(0, portalH - onRoadH) };
  });
  const adminHours = adminPeople.reduce((s, p) => s + p.adminH, 0);
  const adminDays  = adminPeople.reduce((s, p) => s + p.days, 0);

  // Depot bag hours — an allowance, not a clock (see DEPOT_ALLOWANCE_DEFAULT). The part
  // run by a driver who also scanned that period is already inside the outdoor total, so
  // only that part comes off it; a team leader's depot run never was in there.
  const depotInPeriod = (depotRows || []).filter(r => inPeriodISO(r.date));
  const depotHoursOf  = rows => (rows.length * (+depotAllowance.perRun || 0)
                              + rows.reduce((s, r) => s + (r.tally || 0), 0) * (+depotAllowance.perBag || 0)) / 60;
  const waveLogins    = new Set(periodRows.map(r => r.login));
  const depotRunners  = [...new Set(depotInPeriod.map(r => r.login))].sort();
  const depotBags     = depotInPeriod.reduce((s, r) => s + (r.tally || 0), 0);
  const depotHours    = depotHoursOf(depotInPeriod);
  const depotHoursOnRoad = depotHoursOf(depotInPeriod.filter(r => waveLogins.has(r.login)));
  const outdoorNetHours  = Math.max(0, hourSplit.outdoor - depotHoursOnRoad);

  // Every bucket is measured against the same parcel count, so the four rates are
  // directly comparable: what one hour of that kind of time is worth in parcels.
  const pphOn      = h => (h > 0 && s1Parcels ? s1Parcels / h : null);
  const indoorPPH  = pphOn(hourSplit.indoor);
  const outdoorPPH = pphOn(outdoorNetHours);
  const adminPPH   = pphOn(adminHours);
  const bagsPerDepotHour = depotHours > 0 ? depotBags / depotHours : null;

  // Portal P/Hr — Sheet1 parcels ÷ TRUE portal hours (driver-portal start→finish),
  // summed over the driver-days that have a portal shift. Matches the Portal Times tab.
  // Back-office hours come out of the denominator: they're in the same portal but they
  // aren't delivery time, and leaving them in understated the rate by about 8%.
  const portalHrsTotal = Math.max(0, (portalTimes || []).reduce((s, r) => s + (r.totalHours || 0), 0) - adminHours);
  const portalParcelTotal = (portalTimes || []).reduce((s, r) => {
    const cell = sheet1Totals && sheet1Totals.byDate && sheet1Totals.byDate[r.date] && sheet1Totals.byDate[r.date][r.login];
    return s + (cell ? (cell.parcel || 0) : 0);
  }, 0);
  const portalPPH = portalHrsTotal > 0 ? portalParcelTotal / portalHrsTotal : null;

  // ── Inline edit commit ───────────────────────────────────────────────────────
  // Persist the start-time override everywhere: in-memory, localStorage (so it
  // survives a reload) AND the engine Wave_Activity (so corrected hours flow to
  // the Hourly Drivers page and the RCTI). Previously it only updated memory, so
  // the edit silently vanished on reload and never reached Hourly Drivers.
  const commitEdit = (id) => {
    const newStart = editValue.trim();
    const updated = activityData.map(r => {
      if (r.id !== id) return r;
      const finish = effFinishOf(r);
      return { ...r, startIndoorOverride: newStart, startIndoorEffective: newStart, correctedHours: computeHours(newStart, finish) };
    });
    setActivityData(updated);
    saveLocal(updated, activityMeta);
    const periodRows = updated.filter(r => r.date >= selectedPeriod.startISO && r.date <= selectedPeriod.endISO);
    if (periodRows.length) saveToSheet(periodRows, selectedPeriod);
    setEditingId(null);
  };

  // Correct a wrong finish time (e.g. a scanner left logged on till 8pm). Recomputes
  // BOTH hour columns (raw scan-start→finish and corrected legal-start→finish) so the
  // two P/Hr figures fix themselves, and persists like the start edit (localStorage +
  // Wave_Activity → flows to Hourly Drivers / RCTI).
  const commitFinishEdit = (id) => {
    const newFinish = editFinishValue.trim();
    const updated = activityData.map(r => {
      if (r.id !== id) return r;
      return { ...r, endOverride: newFinish,
        correctedHours: computeHours(effStartOf(r), newFinish),
        rawHours: computeHours(r.startIndoorCSV || '', newFinish) };
    });
    setActivityData(updated);
    saveLocal(updated, activityMeta);
    const periodRows = updated.filter(r => r.date >= selectedPeriod.startISO && r.date <= selectedPeriod.endISO);
    if (periodRows.length) saveToSheet(periodRows, selectedPeriod);
    setEditingFinishId(null);
  };

  // Persist + recompute helper shared by the revert actions.
  const persistRows = (updated) => {
    setActivityData(updated);
    saveLocal(updated, activityMeta);
    const pr = updated.filter(r => r.date >= selectedPeriod.startISO && r.date <= selectedPeriod.endISO);
    if (pr.length) saveToSheet(pr, selectedPeriod);
  };
  // Undo a start amendment → fall back to usual/CSV start, recompute corrected hours.
  const revertStart = (id) => persistRows(activityData.map(r => {
    if (r.id !== id) return r;
    const base = r.startIndoorUsual || r.startIndoorCSV || '';
    return { ...r, startIndoorOverride: '', startIndoorEffective: base, startFromPortal: false, correctedHours: computeHours(base, effFinishOf(r)) };
  }));
  // Undo a finish amendment → fall back to the original scanner finish, recompute both hour columns.
  const revertFinish = (id) => persistRows(activityData.map(r => {
    if (r.id !== id) return r;
    const base = r.endOutdoor || r.endIndoor || '';
    return { ...r, endOverride: '', finishFromPortal: false, correctedHours: computeHours(effStartOf(r), base), rawHours: computeHours(r.startIndoorCSV || '', base) };
  }));

  // Auto-correct every late (≥6pm) finish in the current period to that driver's TYPICAL
  // finish — the median of their normal (non-late) days this period. Recomputes hours; each
  // row stays purple + individually revertable. Drivers with no normal day are skipped.
  const autoCorrectLate = () => {
    const inPeriod = r => r.date >= selectedPeriod.startISO && r.date <= selectedPeriod.endISO;
    const typByLogin = {};
    const typFor = login => {
      if (login in typByLogin) return typByLogin[login];
      const mins = periodRows.filter(r => r.login === login && !isLateFinish(r)).map(r => tMin(effFinishOf(r))).filter(x => x != null);
      const md = medianOf(mins);
      return (typByLogin[login] = md != null ? minToHHMM(md) : null);
    };
    let fixed = 0, skipped = 0;
    const updated = activityData.map(r => {
      if (!inPeriod(r) || !isLateFinish(r)) return r;
      const tf = typFor(r.login);
      if (!tf) { skipped++; return r; }
      fixed++;
      return { ...r, endOverride: tf, correctedHours: computeHours(effStartOf(r), tf), rawHours: computeHours(r.startIndoorCSV || '', tf) };
    });
    persistRows(updated);
    addNotification(`Auto-corrected ${fixed} late finish${fixed === 1 ? '' : 'es'} to each driver's typical time${skipped ? ` · ${skipped} skipped (no normal day to learn from)` : ''}`, fixed ? 'success' : 'info');
  };

  // Rename a driver code AND persist to the backend Wave_Activity (so the rename
  // reaches Hourly Drivers, which reads the backend — not just this browser).
  const renameDriver = (f, t) => {
    const count = activityData.filter(r => r.login === f).length;
    if (!count) { addNotification(`No rows found for ${f}`, 'error'); return false; }
    const updated = activityData.map(r => r.login === f ? { ...r, login: t, id: (r.id || '').replace(f, t) } : r);
    setActivityData(updated);
    saveLocal(updated, activityMeta);
    // Save every period that contained a renamed row (saveWaveActivity replaces the period).
    const renamedDates = new Set(activityData.filter(r => r.login === f).map(r => r.date));
    const periods = PERIODS.filter(p => [...renamedDates].some(d => d >= p.startISO && d <= p.endISO));
    (periods.length ? periods : [selectedPeriod]).forEach(p => {
      const pr = updated.filter(r => r.date >= p.startISO && r.date <= p.endISO);
      if (pr.length) saveToSheet(pr, p);
    });
    addNotification(`Renamed ${f} → ${t} across ${count} rows · saved to backend`, 'success');
    return true;
  };

  // Set a driver's start time as the single source of truth: updates the Drivers
  // tab AND recomputes corrected hours across every period, so it flows to the
  // Activity Summary, Hourly Drivers, GCCD Report and Uplift Workbench.
  const setDriverStart = async (login, time) => {
    const lg = (login || '').trim().toUpperCase();
    const t = (time || '').trim();
    if (!lg || !/^\d{1,2}:\d{2}/.test(t)) { addNotification('Pick a driver and a valid time (HH:MM)', 'error'); return false; }
    addNotification(`Setting ${lg} start time to ${t}…`, 'info');
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST',
        body: JSON.stringify({ action: 'setDriverStartTime', login: lg, startTime: t }),
      });
      const d = await res.json();
      if (!d.success) { addNotification(d.error || 'Failed to set start time', 'error'); return false; }
      addNotification(`${lg} start time → ${t}: recomputed ${d.rowsUpdated} day(s) across ${d.filesUpdated} period(s), Drivers tab updated. Reflected on all reference pages.`, 'success');
      loadFromSheet(selectedPeriod);
      return true;
    } catch (e) { addNotification(`Failed: ${e.message}`, 'error'); return false; }
  };

  // Download a single driver's daily activity as a PDF (print-to-PDF window).
  const downloadDriverPdf = (login, colKeys) => {
    const rows = activityData
      .filter(r => r.login === login && r.date >= selectedPeriod.startISO && r.date <= selectedPeriod.endISO)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(r => ({ ...r, pphRaw: rowPphRaw(r), pphOnb: rowPphOnb(r) }));  // computed P/Hr per day
    if (!rows.length) { addNotification('No rows for this driver in the selected period', 'error'); return; }
    const keys = (colKeys && colKeys.length) ? colKeys : DEFAULT_PDF_COLS;
    const cols = PDF_COLS.filter(c => keys.includes(c.key));   // keep canonical order
    const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const fmtD = iso => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || ''); if (!m) return iso || ''; const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${m[3]} ${M[+m[2]-1]} ${m[1]}`; };
    const startOf = r => r.startIndoorOverride || r.startIndoorUsual || r.startIndoorCSV || '—';
    const n0 = v => (v == null || v === '' || isNaN(v)) ? '—' : Math.round(v).toLocaleString('en-AU');
    const h2 = v => (v == null || isNaN(v)) ? '—' : Number(v).toFixed(2);
    const dp = v => (v == null || isNaN(v)) ? '—' : Number(v).toFixed(1);
    const rawVal = (c, r) => c.key === 'start' ? startOf(r) : c.key === 'finish' ? (effFinishOf(r) || '—') : r[c.key];
    const fmtCell = (c, r) => {
      const v = rawVal(c, r);
      if (c.kind === 'hrs') return h2(v);
      if (c.kind === 'num') return n0(v);
      if (c.kind === 'pct') return (v == null || isNaN(v)) ? '—' : Number(v).toFixed(1) + '%';
      if (c.kind === 'dec') return dp(v);
      return esc(v);
    };
    const cls = c => `${c.kind === 'time' ? 'm' : ''} ${c.kind === 'hrs' ? 'm r' : ''} ${(c.kind === 'num' || c.kind === 'pct' || c.kind === 'dec') ? 'r' : ''} ${c.key === 'correctedHours' ? 'b' : ''}`.trim();
    const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
    const period = `${fmtD(selectedPeriod.startISO)} – ${fmtD(selectedPeriod.endISO)}`;
    const head = '<th>Date</th>' + cols.map(c => `<th class="${c.kind === 'time' ? '' : 'r'}">${esc(c.label)}</th>`).join('');
    const body = rows.map(r => '<tr><td>' + esc(fmtD(r.date)) + '</td>' + cols.map(c => `<td class="${cls(c)}">${fmtCell(c, r)}</td>`).join('') + '</tr>').join('');
    const totalCells = cols.map(c => {
      if (c.key === 'pphRaw') {  // weighted: total onboard ÷ total raw hours
        const oh = rows.reduce((s, r) => s + (Number(r.onboardItems) || 0), 0);
        const rh = rows.reduce((s, r) => s + (Number(r.rawHours) || 0), 0);
        return `<td class="r">${rh > 0 ? dp(oh / rh) : '—'}</td>`;
      }
      if (c.key === 'pphOnb') {  // weighted: total Sheet1 parcels ÷ total corrected hours
        const sp = rows.reduce((s, r) => s + (sheet1RowParcels(r) || 0), 0);
        const ch = rows.reduce((s, r) => s + (Number(r.correctedHours) || 0), 0);
        return `<td class="r">${(ch > 0 && sp > 0) ? dp(sp / ch) : '—'}</td>`;  // — when Sheet1 not loaded/no match
      }
      if (!c.sum) return '<td></td>';
      const t = rows.reduce((s, r) => s + (Number(r[c.key]) || 0), 0);
      return `<td class="r">${c.kind === 'hrs' ? h2(t) : n0(t)}</td>`;
    }).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(login)} — Activity ${esc(period)}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  body { font-family: Helvetica, Arial, sans-serif; color:#1f2430; margin:0; font-size:11px; }
  .hdr { background:#1a1a2e; padding:18px 22px; }
  .hdr .b { color:#c9a84c; font-weight:800; font-size:20px; letter-spacing:1px; }
  .hdr .s { color:#cfd3dc; font-size:9px; margin-top:2px; }
  .meta { padding:14px 22px 6px; }
  .meta h1 { font-size:16px; margin:0 0 2px; color:#1a1a2e; }
  .meta .sub { color:#6b7280; font-size:11px; }
  table { border-collapse:collapse; margin:10px 22px; width:calc(100% - 44px); }
  th { background:#1a1a2e; color:#fff; text-align:left; padding:6px 8px; font-size:9.5px; }
  td { border-bottom:1px solid #e7e9ee; padding:6px 8px; }
  td.r, th.r { text-align:right; } td.m { font-family:'Courier New',monospace; } td.b { font-weight:700; color:#1d4ed8; }
  tr.tot td { font-weight:800; border-top:2px solid #1a1a2e; background:#f3f4f6; }
  .note { margin:6px 22px 0; color:#6b7280; font-size:9.5px; }
  .foot { margin:16px 22px 0; color:#9aa0aa; font-size:8.5px; border-top:1px solid #e5e7eb; padding-top:6px; }
</style></head><body>
  <div class="hdr"><div class="b">HOLLYPAUL</div><div class="s">Driver Activity Statement</div></div>
  <div class="meta"><h1>${esc(login)}</h1><div class="sub">Cut-off period: ${esc(period)} · ${rows.length} working days · Generated ${esc(today)}</div></div>
  <table>
    <thead><tr>${head}</tr></thead>
    <tbody>${body}
      <tr class="tot"><td>Total</td>${totalCells}</tr>
    </tbody>
  </table>
  <div class="note">"Start (corrected)" is the legal shift start used to calculate Corrected Hrs (the contracted on-shift time). On-Road Hrs is the scanner's on-road time. P/Hr Raw = onboarded parcels &divide; raw clocked hours (scan-start view); P/Hr Onb = Sheet1 parcels &divide; corrected hours (true legal-start view).</div>
  <div class="foot">Hollypaul · Driver Activity Summary · This statement reflects the figures recorded in our system for the period shown.</div>
  <script>window.onload=function(){window.print();}</script>
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); } else { addNotification('Pop-up blocked — allow pop-ups to download the PDF', 'error'); }
  };

  const thCell = (label, right = false) => (
    <th style={{ padding: '9px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#6b7280', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', textAlign: right ? 'right' : 'left' }}>{label}</th>
  );

  const noData = periodRows.length === 0;

  return (
    <div style={{ display: 'contents' }} onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}>
      {dragging && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(26,58,107,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ background: 'var(--hp-surface)', border: '2px dashed var(--hp-gold)', borderRadius: 16, padding: '48px 64px', textAlign: 'center' }}>
            <Upload size={40} color="var(--hp-gold)" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--hp-gold)' }}>Drop CSV to import</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h2>Driver Activity Summary</h2>
          <p>Full CSV data · editable records · driver scorecards</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="form-select" style={{ minWidth: 260 }} value={selectedPeriod.endISO}
            onChange={e => { const p = PERIODS.find(p => p.endISO === e.target.value); if (p) setSelectedPeriod(p); }}>
            {PERIODS.map(p => <option key={p.endISO} value={p.endISO}>{p.label}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={() => loadFromSheet(selectedPeriod)} disabled={sheetLoading || sheetSaving}>
            <RefreshCw size={13} /> {sheetLoading ? 'Restoring…' : 'Restore from Drive'}
          </button>
          {activityData.length > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={() => saveToSheet(activityData.filter(r => r.date >= selectedPeriod.startISO && r.date <= selectedPeriod.endISO), selectedPeriod)} disabled={sheetSaving || sheetLoading}>
              <Save size={13} /> {sheetSaving ? 'Backing up…' : 'Backup to Drive'}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()} disabled={importing}>
            <Upload size={13} /> {importing ? 'Importing…' : 'Import CSV'}
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,.tsv,.txt" style={{ display: 'none' }}
            onChange={e => { handleImport(e.target.files[0]); e.target.value = ''; }} />
        </div>
      </div>

      <div className="page-body">

        {/* Import meta banner */}
        {activityMeta && (
          <div style={{ background: 'rgba(34,197,94,.06)', border: '1px solid rgba(34,197,94,.2)', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Activity size={14} color="var(--hp-green)" />
            <span style={{ color: 'var(--hp-green)', fontWeight: 600 }}>Loaded</span>
            <span style={{ color: 'var(--hp-dim)' }}>·</span>
            <span>{activityMeta.filename}</span>
            <span style={{ color: 'var(--hp-dim)' }}>·</span>
            <span>{activityMeta.dateRange}</span>
            <span style={{ color: 'var(--hp-dim)' }}>·</span>
            <span>{activityMeta.records} records</span>
            <span style={{ color: 'var(--hp-dim)' }}>·</span>
            <span style={{ color: 'var(--hp-green)' }}>{activityMeta.corrected} start times corrected</span>
            <button style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', fontSize: '1.1rem', lineHeight: 1 }} onClick={() => setActivityMeta(null)}>×</button>
          </div>
        )}

        {/* Period summary chips + P/Hr comparison */}
        {!noData && (
          <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: 12, marginBottom: 12 }}>
            <Chip label="Drivers"         value={allDrivers.length}                                        accent="var(--hp-gold)" />
            <Chip label="Driver-Days"     value={periodRows.length}                                        accent="var(--hp-blue)" />
            <Chip label="Wave Onboard"    value={totalOnboard.toLocaleString()}                            accent="#6366f1" />
            <Chip label="Sheet1 Parcels"  value={s1Total != null ? s1Total.toLocaleString() : '…'}        accent="var(--hp-green)" sub="Source of truth" />
            <Chip label="Portal P/Hr"     value={portalPPH != null ? dp(portalPPH) : '…'}                  accent="#0d9488" sub="Sheet1 ÷ portal hrs (excl. admin)" />
            <Chip label="Carded"          value={totalCarded.toLocaleString()}                             accent="var(--hp-amber)" sub={fleetCrdPct != null ? dp(fleetCrdPct) + '% card rate' : ''} />
            <Chip label="MIT"             value={totalMIT.toLocaleString()}                                accent="var(--hp-red)" />
          </div>

          {/* Where the hours go — indoor · outdoor · admin · depot bags */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '18px 22px', marginBottom: 12, borderLeft: '4px solid #0d9488' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                Where the Hours Go — and What Each Hour Delivers
              </div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>
                each rate is the same {s1Parcels ? s1Parcels.toLocaleString() : '…'} parcels over that bucket's hours
              </div>
            </div>
            <div className="stack-mobile hours-band" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0 }}>
              <HourBucket
                first label="Driver — indoor (actual)" accent="#6366f1"
                hours={hourSplit.indoor} rate={indoorPPH} rateLabel="parcels per indoor hour"
                lines={[
                  `${hourSplit.indoorDays} driver-days · legal start → first outdoor scan`,
                  'Sorting and loading, before the run leaves',
                  hourSplit.indoorSkipped ? `${hourSplit.indoorSkipped} day${hourSplit.indoorSkipped === 1 ? '' : 's'} skipped — no usable scan pair` : null,
                ]}
              />
              <HourBucket
                label="Driver — outdoor (actual)" accent="var(--hp-green)"
                hours={outdoorNetHours} rate={outdoorPPH} rateLabel="parcels per on-road hour"
                lines={[
                  `${hourSplit.outdoorDays} driver-days · first outdoor scan → finish`,
                  depotHoursOnRoad > 0
                    ? `${hrs(hourSplit.outdoor)} on the road, less ${hrs(depotHoursOnRoad)} of depot bag work`
                    : 'On the road, delivering',
                ]}
              />
              <HourBucket
                label="Admin" accent="var(--hp-gold)"
                hours={adminHours} rate={adminPPH} rateLabel="parcels per admin hour"
                lines={[
                  adminPeople.map(p => `${p.name} ${dp(p.adminH)}`).join(' · '),
                  `${adminDays} shifts · portal hours, less any round they drove`,
                  portalTimes == null ? 'Loading portal hours…' : null,
                ]}
              />
              <HourBucket
                last label="Depot bags (allowance)" accent="var(--hp-amber)"
                hours={depotHours} rate={bagsPerDepotHour} rateLabel="bags per hour"
                lines={[
                  depotRows == null
                    ? 'Loading depot runs…'
                    : `${depotInPeriod.length} runs · ${depotBags.toLocaleString()} bags · ${depotRunners.length} driver${depotRunners.length === 1 ? '' : 's'}`,
                  'Nobody clocks this separately — set the allowance:',
                ]}
              >
                <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  {[['perRun', 'min / run'], ['perBag', 'min / bag']].map(([key, label]) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6b7280' }}>
                      <input type="number" min="0" step="0.5" value={depotAllowance[key]}
                        onChange={e => setDepotAllowance(a => ({ ...a, [key]: e.target.value }))}
                        style={{ width: 52, padding: '3px 6px', fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 5, fontVariantNumeric: 'tabular-nums' }} />
                      {label}
                    </label>
                  ))}
                </div>
              </HourBucket>
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 14, paddingTop: 10, borderTop: '1px solid #f3f4f6', lineHeight: 1.5 }}>
              Indoor and outdoor come off the AP scanner (Wave); admin comes off the driver portal. Two clocks, so they are shown
              side by side and never added into one number. Admin hours are out of the Portal P/Hr tile above — they are paid hours,
              but they are not delivery hours.
            </div>
          </div>

          {/* P/Hr comparison tile */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '18px 22px', marginBottom: 20, borderLeft: '4px solid var(--hp-gold)' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>
              Parcels Per Hour — Raw Data vs Engine (Legal Shift Start)
            </div>
            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0 }}>
              {/* Raw CSV */}
              <div style={{ borderRight: '1px solid #e5e7eb', paddingRight: 24 }}>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>AP Raw Data P/Hr</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#6366f1', fontVariantNumeric: 'tabular-nums' }}>
                  {fleetRawPPH != null ? dp(fleetRawPPH, 2) : '—'}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                  {totalOnboard.toLocaleString()} onboard ÷ {hrs(totalRawHours)} raw hours
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                  Wave CSV only · AP's scan-start view
                </div>
              </div>
              {/* Engine corrected */}
              <div style={{ borderRight: '1px solid #e5e7eb', padding: '0 24px' }}>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>Engine P/Hr (Legal)</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--hp-green)', fontVariantNumeric: 'tabular-nums' }}>
                  {fleetPPH != null ? dp(fleetPPH, 2) : '—'}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                  {s1Total != null ? s1Total.toLocaleString() : '…'} Sheet1 parcels ÷ {hrs(totalHours)} corrected hours
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                  Sheet1 source of truth · legal shift start
                </div>
              </div>
              {/* Variance */}
              <div style={{ paddingLeft: 24 }}>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>Variance</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: pphDiff != null && Math.abs(pphDiff) > 0.05 ? (pphDiff > 0 ? 'var(--hp-amber)' : 'var(--hp-green)') : 'var(--hp-dim)', fontVariantNumeric: 'tabular-nums' }}>
                  {pphDiff != null ? (pphDiff > 0 ? '+' : '') + dp(pphDiff, 2) : '—'}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                  p/hr difference (raw − engine)
                </div>
                {pphDiff != null && pphDiff > 0.05 && (
                  <div style={{ marginTop: 8, fontSize: 11, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 10px', color: '#92400e', fontWeight: 600, lineHeight: 1.4 }}>
                    AP overstates P/Hr by {dp(pphDiff, 2)} — pre-scan sorting time excluded from AP figures
                  </div>
                )}
                {pphDiff != null && pphDiff < -0.05 && (
                  <div style={{ marginTop: 8, fontSize: 11, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '6px 10px', color: '#166534', fontWeight: 600, lineHeight: 1.4 }}>
                    Engine P/Hr exceeds AP by {dp(Math.abs(pphDiff), 2)} — Sheet1 captures more parcel volume
                  </div>
                )}
              </div>
            </div>
          </div>
          </>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid var(--hp-border)', marginBottom: 20 }}>
          {[['log','Activity Log'],['scorecards','Driver Scorecards'],['analysis','Analysis & Reconciliation'],['portal','Portal Times']].map(([id, label]) => (
            <button key={id} onClick={() => setActiveTab(id)} style={{ padding: '9px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '.85rem', fontWeight: activeTab === id ? 700 : 500, color: activeTab === id ? 'var(--hp-gold)' : 'var(--hp-dim)', borderBottom: activeTab === id ? '2px solid var(--hp-gold)' : '2px solid transparent', marginBottom: -2, transition: 'all .15s' }}>
              {label}
            </button>
          ))}
          {!noData && <span style={{ marginLeft: 'auto', fontSize: '.75rem', color: 'var(--hp-dim)', alignSelf: 'center' }}>{periodRows.length} records for {selectedPeriod.label}</span>}
        </div>

        {/* Empty state (CSV-driven tabs only — Portal Times has its own data source) */}
        {noData && activeTab !== 'portal' && (
          <div style={{ textAlign: 'center', padding: '64px 20px', color: 'var(--hp-dim)' }}>
            <Upload size={40} style={{ marginBottom: 12, opacity: .3 }} />
            <p style={{ fontSize: '.9rem', marginBottom: 8 }}>No data for this period — drag a CSV file onto this page or click Import CSV</p>
            <p style={{ fontSize: '.78rem', opacity: .7 }}>{activityData.length > 0 ? `${activityData.length} records loaded for a different period — change the period selector above` : ''}</p>
          </div>
        )}

        {/* ── Portal Times tab — start/finish/break from the drivers' portal + Sheet1 parcels ── */}
        {activeTab === 'portal' && (
          <>
            {decisionCount > 0 && (
              <div style={{ border: '1px solid var(--hp-gold)', background: 'rgba(201,168,76,.08)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '.8rem' }}>
                <strong>⚖ {decisionCount} day{decisionCount === 1 ? '' : 's'} in this period were adjudicated in the Uplift Workbench.</strong>{' '}
                The portal times below are untouched — they're still exactly what the driver clocked. Where the workbench decided the day differently,
                the Hours cell carries a ⚖ and the decided figure; hover it for who decided, when and why. Pay followed the decision, not this column.
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              <input className="form-input" placeholder="Search driver…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 200 }} />
              <select className="form-select" value={filterDriver} onChange={e => setFilterDriver(e.target.value)}>
                <option value="">All drivers</option>
                {portalDrivers.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} title="Filter to a date range within the period">
                <span style={{ fontSize: '.75rem', color: 'var(--hp-dim)' }}>From</span>
                <input className="form-input" type="date" value={portalFrom} min={selectedPeriod.startISO} max={selectedPeriod.endISO}
                  onChange={e => setPortalFrom(e.target.value)} style={{ width: 150 }} />
                <span style={{ fontSize: '.75rem', color: 'var(--hp-dim)' }}>to</span>
                <input className="form-input" type="date" value={portalTo} min={selectedPeriod.startISO} max={selectedPeriod.endISO}
                  onChange={e => setPortalTo(e.target.value)} style={{ width: 150 }} />
                {(portalFrom || portalTo) && (
                  <button className="btn btn-ghost btn-sm" type="button" onClick={() => { setPortalFrom(''); setPortalTo(''); }} title="Clear date range">Clear</button>
                )}
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => { fetchPortalTimes(); fetchSheet1Totals(); }} disabled={portalLoading}>
                {portalLoading ? 'Loading…' : 'Refresh'}
              </button>
              <span style={{ marginLeft: 'auto', fontSize: '.75rem', color: 'var(--hp-dim)' }}>
                Live portal data · {selectedPeriod.label}
              </span>
            </div>

            <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginBottom: 12, lineHeight: 1.5 }}>
              True start / finish / break captured from the driver portal, with Sheet1 parcels and parcels‑per‑hour (parcels ÷ portal hours). One row per driver per day.
            </div>

            {portalLoading && (!portalTimes || portalTimes.length === 0) ? (
              <div style={{ textAlign: 'center', padding: '48px', color: 'var(--hp-dim)' }}>Loading portal times…</div>
            ) : portalDaily.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: 'var(--hp-dim)' }}>No portal shifts recorded for this period yet.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ minWidth: 820 }}>
                  <thead>
                    <tr>
                      <th>Date</th><th>Driver</th>
                      <th style={{ textAlign: 'center' }}>Start</th>
                      <th style={{ textAlign: 'center' }}>Finish</th>
                      <th style={{ textAlign: 'center' }}>Break</th>
                      <th style={{ textAlign: 'right' }}>Hours</th>
                      <th style={{ textAlign: 'right' }}>KM</th>
                      <th style={{ textAlign: 'right' }}>Parcels</th>
                      <th style={{ textAlign: 'right' }}>Parcels / Hr</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portalDaily.map((r, i) => (
                      <tr key={r.login + r.date + i}>
                        <td style={{ fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{formatMed(r.date)}</td>
                        <td><strong>{r.login}</strong>{r.name && r.name !== r.login ? <span style={{ color: 'var(--hp-dim)', fontSize: '.78rem' }}> · {r.name}</span> : ''}</td>
                        <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{r.shiftStart || '—'}</td>
                        <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{r.finishTime || '—'}</td>
                        <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', color: r.breakMin ? 'var(--hp-text)' : 'var(--hp-dim)' }}>{r.breakMin ? `${r.breakMin} min` : '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                          {r.totalHours ? Number(r.totalHours).toFixed(2) : '—'}
                          {hourDecisions[`${r.login}|${r.date}`] && (() => {
                            const dec = hourDecisions[`${r.login}|${r.date}`];
                            return (
                              <span
                                title={`Uplift decision applied — this day was paid on ${dec.hours.toFixed(2)} hrs (${dec.source}${dec.startTime ? ' start ' + dec.startTime : ''}), decided by ${dec.decidedBy || 'admin'} on ${(dec.decidedAt || '').slice(0, 10)}.${dec.note ? ' Note: ' + dec.note : ''} The portal time shown here is unchanged.`}
                                style={{ marginLeft: 4, color: 'var(--hp-gold)', fontWeight: 700, cursor: 'help' }}>
                                ⚖{dec.hours.toFixed(2) !== Number(r.totalHours || 0).toFixed(2) ? ' ' + dec.hours.toFixed(2) : ''}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{r.totalKm ? Math.round(r.totalKm).toLocaleString() : '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{r.parcels ? r.parcels.toLocaleString() : '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--hp-gold)' }}>{r.pph != null ? r.pph.toFixed(1) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--hp-border)', fontWeight: 700 }}>
                      <td colSpan={5} style={{ textAlign: 'right', color: 'var(--hp-dim)' }}>Totals ({portalDaily.length} shifts)</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{portalTotals.hours.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{Math.round(portalTotals.km).toLocaleString()}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{portalTotals.parcels.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)' }}>{portalTotals.hours > 0 ? (portalTotals.parcels / portalTotals.hours).toFixed(1) : '—'}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </>
        )}

        {/* ── Activity Log tab ── */}
        {activeTab === 'log' && !noData && (
          <>
            {decisionCount > 0 && (
              <div style={{ border: '1px solid var(--hp-gold)', background: 'rgba(201,168,76,.08)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '.8rem' }}>
                <strong>⚖ {decisionCount} day{decisionCount === 1 ? '' : 's'} in this period were adjudicated in the Uplift Workbench.</strong>{' '}
                Scanner hours here are as imported — remember the start time is the driver's usual-start rule, not a measurement. Where a day was
                decided differently, pay followed the decision. See the Uplift Workbench for the call and the reason.
              </div>
            )}
            {/* Filter bar */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <input className="form-input" placeholder="Search driver / depot…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 200 }} />
              <select className="form-select" value={filterDriver} onChange={e => { setFilterDriver(e.target.value); }}>
                <option value="">All drivers</option>
                {allDrivers.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select className="form-select" value={filterDepot} onChange={e => setFilterDepot(e.target.value)}>
                <option value="">All depots</option>
                {allDepots.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <button className={`btn btn-sm ${lateOnly ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setLateOnly(v => !v)} title="Show only rows finishing at/after 6pm">
                <AlertTriangle size={13} /> Late finishes{(() => { const n = periodRows.filter(isLateFinish).length; return n ? ` (${n})` : ''; })()}
              </button>
              <button className="btn btn-secondary btn-sm" disabled={!periodRows.some(isLateFinish)}
                title="Set each late (red) finish to that driver's typical finish from their normal days, and recompute hours"
                onClick={() => { const n = periodRows.filter(isLateFinish).length; if (n && window.confirm(`Auto-correct ${n} late finish${n === 1 ? '' : 'es'} to each driver's typical finish (median of their normal days)? Each one stays revertable.`)) autoCorrectLate(); }}>
                <RefreshCw size={13} /> Auto-correct late
              </button>
              {(filterDriver || filterDepot || search || lateOnly) && (
                <button className="btn btn-secondary btn-sm" onClick={() => { setFilterDriver(''); setFilterDepot(''); setSearch(''); setLateOnly(false); }}>Clear filters</button>
              )}
              <RenameDriverBtn allDrivers={allDrivers} onRename={renameDriver} />
              <SetStartTimeBtn allDrivers={allDrivers} onSet={setDriverStart} />
              {(() => {
                const logins = [...new Set(filteredRows.map(r => r.login))];
                return logins.length === 1 ? (
                  <PdfDownloadBtn login={logins[0]} onDownload={cols => downloadDriverPdf(logins[0], cols)} />
                ) : null;
              })()}
              {recoverableInView > 0 && (
                <span title="Days in view with a missing start/finish that the driver portal can fill. Click the green tick in the Corr. Hrs column to accept each one." style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '.75rem', color: 'var(--hp-green)', alignSelf: 'center' }}>
                  <Check size={13} /> {recoverableInView} recoverable from portal
                </span>
              )}
              <span style={{ marginLeft: recoverableInView > 0 ? 12 : 'auto', fontSize: '.78rem', color: 'var(--hp-dim)', alignSelf: 'center' }}>{filteredRows.length} rows</span>
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ minWidth: 1200 }}>
                  <thead>
                    <tr>
                      {thCell('Driver')}
                      {thCell('Date')}
                      {thCell('Depot')}
                      {thCell('Start (corrected)')}
                      {thCell('End Outdoor')}
                      {thCell('Corr. Hrs', true)}
                      {thCell('P/Hr Raw', true)}
                      {thCell('P/Hr Onb', true)}
                      {thCell('Onboard', true)}
                      {thCell('Delivered', true)}
                      {thCell('Del %', true)}
                      {thCell('Carded', true)}
                      {thCell('Crd %', true)}
                      {thCell('Undel', true)}
                      {thCell('MIT', true)}
                      {thCell('Pickup', true)}
                      {thCell('On-Rd Hrs', true)}
                      {thCell('Stops/Hr', true)}
                      {thCell('Art/Stop', true)}
                      {thCell('Stops/Run', true)}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map(row => {
                      const isEditing = editingId === row.id;
                      const startLabel = row.startIndoorOverride || row.startIndoorUsual || row.startIndoorCSV || '—';
                      const startColor = row.startIndoorOverride ? 'var(--hp-purple)' : row.startIndoorUsual ? 'var(--hp-green)' : 'var(--hp-amber)';
                      const fill = portalFillFor(row);   // portal recovery for a missing start/finish day
                      const portalTag = <span title="Recovered from the driver portal (Driver_Logs)" style={{ fontSize: '.58rem', color: 'var(--hp-green)', border: '1px solid var(--hp-green)', borderRadius: 4, padding: '0 3px', textTransform: 'uppercase', letterSpacing: '.04em' }}>portal</span>;
                      return (
                        <tr key={row.id} style={isLateFinish(row) ? { background: 'rgba(220,38,38,.10)' } : undefined} title={isLateFinish(row) ? 'Finished at/after 6pm — check the finish time' : undefined}>
                          <td style={{ fontFamily: 'var(--font-mono)', fontSize: '.78rem', fontWeight: 600 }}>{row.login}</td>
                          <td style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>{row.date}</td>
                          <td style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>{row.depot || '—'}</td>
                          {/* Editable start time */}
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {isEditing ? (
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <input type="text" className="form-input" style={{ width: 90, fontFamily: 'var(--font-mono)', fontSize: '.78rem', padding: '2px 6px' }}
                                  value={editValue} onChange={e => setEditValue(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') commitEdit(row.id); if (e.key === 'Escape') setEditingId(null); }}
                                  autoFocus />
                                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-green)', padding: 2 }} onClick={() => commitEdit(row.id)}><Check size={13} /></button>
                                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-red)',   padding: 2 }} onClick={() => setEditingId(null)}><X size={13} /></button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}
                                onClick={() => { setEditingId(row.id); setEditValue(startLabel === '—' ? '' : startLabel); }}>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.78rem', color: startColor }}>{startLabel}</span>
                                {row.startFromPortal && portalTag}
                                <Edit3 size={11} color="var(--hp-dim)" style={{ opacity: .5 }} />
                                {row.startIndoorOverride && <button title="Revert to usual/scanner start" onClick={e => { e.stopPropagation(); revertStart(row.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-amber)', padding: 0, display: 'flex' }}><RotateCcw size={11} /></button>}
                              </div>
                            )}
                          </td>
                          {/* Editable finish time */}
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {editingFinishId === row.id ? (
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <input type="text" className="form-input" style={{ width: 90, fontFamily: 'var(--font-mono)', fontSize: '.78rem', padding: '2px 6px' }}
                                  value={editFinishValue} onChange={e => setEditFinishValue(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') commitFinishEdit(row.id); if (e.key === 'Escape') setEditingFinishId(null); }}
                                  autoFocus />
                                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-green)', padding: 2 }} onClick={() => commitFinishEdit(row.id)}><Check size={13} /></button>
                                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-red)',   padding: 2 }} onClick={() => setEditingFinishId(null)}><X size={13} /></button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}
                                onClick={() => { const f = effFinishOf(row); setEditingFinishId(row.id); setEditFinishValue(f); }}>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.78rem', color: row.endOverride ? 'var(--hp-purple)' : (isLateFinish(row) ? 'var(--hp-red)' : 'var(--hp-dim)') }}>{effFinishOf(row) || '—'}</span>
                                {row.finishFromPortal && portalTag}
                                <Edit3 size={11} color="var(--hp-dim)" style={{ opacity: .5 }} />
                                {row.endOverride && <button title="Revert to scanner finish" onClick={e => { e.stopPropagation(); revertFinish(row.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-amber)', padding: 0, display: 'flex' }}><RotateCcw size={11} /></button>}
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '.78rem', fontWeight: 600, color: row.correctedHours ? 'var(--hp-blue)' : 'var(--hp-dim)' }}>
                            {row.correctedHours != null
                              ? row.correctedHours.toFixed(2)
                              : fill
                                ? <button
                                    title={`Portal clock times ${fill.newStart}→${fill.newFinish} = ${fill.hours.toFixed(2)}h. Click to accept as this day's hours.`}
                                    onClick={() => acceptPortal(row.id)}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(16,185,129,.12)', border: '1px solid var(--hp-green)', color: 'var(--hp-green)', borderRadius: 6, padding: '1px 7px', fontFamily: 'var(--font-mono)', fontSize: '.72rem', fontWeight: 600, cursor: 'pointer' }}>
                                    <Check size={10} /> {fill.hours.toFixed(2)}
                                  </button>
                                : '—'}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '.78rem', color: '#6366f1' }}>{dp(rowPphRaw(row))}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '.78rem', fontWeight: 600, color: rowPphOnb(row) != null ? 'var(--hp-green)' : 'var(--hp-dim)' }}>{dp(rowPphOnb(row))}</td>
                          <td style={{ textAlign: 'right', fontSize: '.78rem' }}>{num(row.onboardItems)}</td>
                          <td style={{ textAlign: 'right', fontSize: '.78rem', fontWeight: 600, color: 'var(--hp-green)' }}>{num(row.deliveredItems)}</td>
                          <td style={{ textAlign: 'right', fontSize: '.78rem' }}>{pct(row.deliveredPct)}</td>
                          <td style={{ textAlign: 'right', fontSize: '.78rem' }}>{num(row.cardedItems)}</td>
                          <td style={{ textAlign: 'right', fontSize: '.78rem', color: row.cardedPct > 15 ? 'var(--hp-amber)' : undefined }}>{pct(row.cardedPct)}</td>
                          <td style={{ textAlign: 'right', fontSize: '.78rem', color: row.undeliveredItems > 0 ? 'var(--hp-red)' : 'var(--hp-dim)' }}>{num(row.undeliveredItems)}</td>
                          <td style={{ textAlign: 'right', fontSize: '.78rem', color: row.missingInTransit > 0 ? 'var(--hp-red)' : 'var(--hp-dim)' }}>{num(row.missingInTransit)}</td>
                          <td style={{ textAlign: 'right', fontSize: '.78rem' }}>{num(row.pickupItems)}</td>
                          <td style={{ textAlign: 'right', fontSize: '.78rem' }}>{dp(row.avgOnRoadHours)}</td>
                          <td style={{ textAlign: 'right', fontSize: '.78rem' }}>{dp(row.avgStopsPerOnRoadHour)}</td>
                          <td style={{ textAlign: 'right', fontSize: '.78rem' }}>{dp(row.avgArticlesPerStop)}</td>
                          <td style={{ textAlign: 'right', fontSize: '.78rem' }}>{dp(row.avgStopsPerRun)}</td>
                        </tr>
                      );
                    })}
                    {(() => {
                      const f = filteredRows;
                      if (!f.length) return null;
                      const S = k => f.reduce((s, r) => s + (Number(r[k]) || 0), 0);
                      const N = v => (v == null || isNaN(v)) ? '—' : Math.round(v).toLocaleString();
                      const onb = S('onboardItems');
                      const totRaw = S('rawHours'), totCorr = S('correctedHours');
                      const s1Sum = f.reduce((s, r) => s + (sheet1RowParcels(r) || 0), 0);
                      const pphRawTot = totRaw  > 0 ? onb   / totRaw  : null;
                      const pphOnbTot = (totCorr > 0 && s1Sum > 0) ? s1Sum / totCorr : null;  // — when Sheet1 not loaded/no match
                      const D1 = v => (v == null || isNaN(v)) ? '—' : Number(v).toFixed(1);
                      const tdR = { textAlign: 'right', fontSize: '.78rem', fontWeight: 700 };
                      return (
                        <tr style={{ background: 'var(--hp-bg)', borderTop: '2px solid var(--hp-border)' }}>
                          <td colSpan={5} style={{ fontSize: '.78rem', fontWeight: 700 }}>Total · {f.length} day{f.length !== 1 ? 's' : ''}</td>
                          <td style={{ ...tdR, fontFamily: 'var(--font-mono)', color: 'var(--hp-blue)' }}>{S('correctedHours').toFixed(2)}</td>
                          <td style={{ ...tdR, fontFamily: 'var(--font-mono)', color: '#6366f1' }}>{D1(pphRawTot)}</td>
                          <td style={{ ...tdR, fontFamily: 'var(--font-mono)', color: 'var(--hp-green)' }}>{D1(pphOnbTot)}</td>
                          <td style={tdR}>{N(onb)}</td>
                          <td style={{ ...tdR, color: 'var(--hp-green)' }}>{N(S('deliveredItems'))}</td>
                          <td style={tdR}>{onb ? (S('deliveredItems') / onb * 100).toFixed(1) + '%' : '—'}</td>
                          <td style={tdR}>{N(S('cardedItems'))}</td>
                          <td style={tdR}>{onb ? (S('cardedItems') / onb * 100).toFixed(1) + '%' : '—'}</td>
                          <td style={tdR}>{N(S('undeliveredItems'))}</td>
                          <td style={tdR}>{N(S('missingInTransit'))}</td>
                          <td style={tdR}>{N(S('pickupItems'))}</td>
                          <td style={{ ...tdR, fontFamily: 'var(--font-mono)' }}>{S('avgOnRoadHours').toFixed(1)}</td>
                          <td style={tdR}>—</td>
                          <td style={tdR}>—</td>
                          <td style={tdR}>—</td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{ marginTop: 8, fontSize: '.72rem', color: 'var(--hp-dim)' }}>
              Start colour: <span style={{ color: 'var(--hp-green)' }}>green</span> = corrected from Drivers tab ·&nbsp;
              <span style={{ color: 'var(--hp-amber)' }}>amber</span> = raw CSV ·&nbsp;
              <span style={{ color: 'var(--hp-purple)' }}>purple</span> = manually overridden · Click any start time to edit
            </div>
          </>
        )}

        {/* ── Driver Scorecards tab ── */}
        {activeTab === 'scorecards' && !noData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {scorecards.map(sc => {
              const expanded = expandedCard === sc.login;
              const delColor = sc.delPct == null ? 'var(--hp-dim)' : sc.delPct >= 90 ? 'var(--hp-green)' : sc.delPct >= 80 ? 'var(--hp-amber)' : 'var(--hp-red)';
              const crdColor = sc.crdPct == null ? 'var(--hp-dim)' : sc.crdPct <= 8 ? 'var(--hp-green)' : sc.crdPct <= 15 ? 'var(--hp-amber)' : 'var(--hp-red)';
              const pphColor = sc.pph == null ? 'var(--hp-dim)' : sc.pph >= 15 ? 'var(--hp-green)' : sc.pph >= 10 ? 'var(--hp-amber)' : 'var(--hp-red)';
              return (
                <div key={sc.login} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  {/* Card header row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr 1fr 1fr 1fr 1fr 1fr 44px', gap: 0, alignItems: 'center', padding: '12px 16px', cursor: 'pointer', borderBottom: expanded ? '1px solid var(--hp-border)' : 'none' }}
                    onClick={() => setExpandedCard(expanded ? null : sc.login)}>
                    <div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.85rem', fontWeight: 700 }}>{sc.login}</div>
                      <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)' }}>{sc.depot} · {sc.days} days</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Delivered</div>
                      <div style={{ fontWeight: 700, color: 'var(--hp-green)' }}>{sc.totalDelivered.toLocaleString()}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Del %</div>
                      <div style={{ fontWeight: 700, color: delColor }}>{dp(sc.delPct)}%</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Carded %</div>
                      <div style={{ fontWeight: 700, color: crdColor }}>{dp(sc.crdPct)}%</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.06em' }}>P/Hr (Engine)</div>
                      <div style={{ fontWeight: 700, color: pphColor }}>{dp(sc.pph)}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.06em' }}>P/Hr (Raw)</div>
                      <div style={{ fontWeight: 700, color: '#6366f1' }}>{dp(sc.rawPph)}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Avg Hrs/Day</div>
                      <div style={{ fontWeight: 700 }}>{dp(sc.avgHrs)}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.06em' }}>MIT</div>
                      <div style={{ fontWeight: 700, color: sc.totalMIT > 0 ? 'var(--hp-red)' : 'var(--hp-dim)' }}>{sc.totalMIT}</div>
                    </div>
                    <div style={{ textAlign: 'center', color: 'var(--hp-dim)' }}>
                      {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {expanded && (
                    <div style={{ padding: '16px 20px', background: 'var(--hp-surface)' }}>
                      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
                        {[
                          { label: 'Total Onboard',    value: sc.totalOnboard.toLocaleString(),   accent: '#6366f1' },
                          { label: 'Total Delivered',  value: sc.totalDelivered.toLocaleString(),  accent: 'var(--hp-green)' },
                          { label: 'Total Carded',     value: sc.totalCarded.toLocaleString(),     accent: 'var(--hp-amber)' },
                          { label: 'Undelivered',      value: sc.totalUndel.toLocaleString(),      accent: sc.totalUndel > 0 ? 'var(--hp-red)' : 'var(--hp-dim)' },
                          { label: 'Missing In Transit', value: sc.totalMIT.toLocaleString(),      accent: sc.totalMIT > 0 ? 'var(--hp-red)' : 'var(--hp-dim)' },
                          { label: 'Pickup Items',     value: sc.totalPickup.toLocaleString(),     accent: 'var(--hp-blue)' },
                          { label: 'Total Corrected Hrs', value: hrs(sc.totalHours),              accent: 'var(--hp-blue)' },
                          { label: 'Total Raw Hrs (CSV)',  value: hrs(sc.totalRawHours),           accent: '#6366f1' },
                          { label: 'P/Hr Raw (AP view)',   value: dp(sc.rawPph),                  accent: '#6366f1' },
                          { label: 'P/Hr Engine (Legal)',  value: dp(sc.pph),                     accent: pphColor },
                          { label: 'Hrs Diff (legal−raw)', value: sc.totalHours != null && sc.totalRawHours != null ? dp(sc.totalHours - sc.totalRawHours, 2) + ' h' : '—', accent: 'var(--hp-amber)' },
                          { label: 'Avg Stops/Hr',     value: dp(sc.avgStopsHr),                  accent: '#6366f1' },
                          { label: 'Avg Articles/Stop', value: dp(sc.avgArtStop),                 accent: '#6366f1' },
                          { label: 'Avg Stops/Run',    value: dp(sc.avgStopsRun),                 accent: '#6366f1' },
                          { label: 'Delivery Rate',    value: dp(sc.delPct) + '%',                accent: delColor },
                          { label: 'Card Rate',        value: dp(sc.crdPct) + '%',                accent: crdColor },
                        ].map(({ label, value, accent }) => (
                          <div key={label} style={{ background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '10px 14px' }}>
                            <div style={{ fontSize: 10, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>{label}</div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: accent, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
                          </div>
                        ))}
                      </div>
                      {/* Daily breakdown for this driver */}
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.6rem', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 8, letterSpacing: '.08em' }}>Daily Breakdown</div>
                      <div style={{ overflowX: 'auto' }}>
                        <table className="data-table" style={{ minWidth: 700 }}>
                          <thead>
                            <tr>
                              {['Date','Start','End Out','Hrs','Onboard','Delivered','Del %','Carded','Crd %','Undel','MIT','Stops/Hr','Art/Stop'].map(h => (
                                <th key={h} style={{ padding: '7px 10px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#6b7280', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {periodRows.filter(r => r.login === sc.login).sort((a,b) => a.date.localeCompare(b.date)).map(r => (
                              <tr key={r.id}>
                                <td style={{ fontSize: '.75rem', whiteSpace: 'nowrap' }}>{r.date}</td>
                                <td style={{ fontSize: '.75rem', fontFamily: 'var(--font-mono)', color: r.startIndoorOverride ? 'var(--hp-purple)' : r.startIndoorUsual ? 'var(--hp-green)' : 'var(--hp-amber)' }}>{r.startIndoorEffective || '—'}</td>
                                <td style={{ fontSize: '.75rem', fontFamily: 'var(--font-mono)', color: 'var(--hp-dim)' }}>{r.endOutdoor || '—'}</td>
                                <td style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--hp-blue)' }}>{r.correctedHours != null ? r.correctedHours.toFixed(2) : '—'}</td>
                                <td style={{ fontSize: '.75rem' }}>{num(r.onboardItems)}</td>
                                <td style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--hp-green)' }}>{num(r.deliveredItems)}</td>
                                <td style={{ fontSize: '.75rem', color: r.deliveredPct >= 90 ? 'var(--hp-green)' : r.deliveredPct >= 80 ? 'var(--hp-amber)' : 'var(--hp-red)' }}>{pct(r.deliveredPct)}</td>
                                <td style={{ fontSize: '.75rem' }}>{num(r.cardedItems)}</td>
                                <td style={{ fontSize: '.75rem', color: r.cardedPct > 15 ? 'var(--hp-amber)' : 'var(--hp-dim)' }}>{pct(r.cardedPct)}</td>
                                <td style={{ fontSize: '.75rem', color: r.undeliveredItems > 0 ? 'var(--hp-red)' : 'var(--hp-dim)' }}>{num(r.undeliveredItems)}</td>
                                <td style={{ fontSize: '.75rem', color: r.missingInTransit > 0 ? 'var(--hp-red)' : 'var(--hp-dim)' }}>{num(r.missingInTransit)}</td>
                                <td style={{ fontSize: '.75rem' }}>{dp(r.avgStopsPerOnRoadHour)}</td>
                                <td style={{ fontSize: '.75rem' }}>{dp(r.avgArticlesPerStop)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {/* ── Analysis & Reconciliation tab ── */}
        {activeTab === 'analysis' && (
          <AnalysisTab
            periodRows={periodRows}
            allDrivers={allDrivers}
            selectedPeriod={selectedPeriod}
            breakdownBucket={breakdownBucket}
            setBreakdownBucket={setBreakdownBucket}
            breakdownDriver={breakdownDriver}
            setBreakdownDriver={setBreakdownDriver}
            sheet1Totals={sheet1Totals}
            reconcLoading={reconcLoading}
            onRefreshReconc={() => { setSheet1Totals(null); fetchSheet1Totals(); }}
          />
        )}

      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ── RenameDriverBtn ───────────────────────────────────────────────────────────
// ── PdfDownloadBtn — pick columns, then download ──────────────────────────────
function PdfDownloadBtn({ login, onDownload }) {
  const LSK = 'hp-das-pdf-cols';
  const MIGK = 'hp-das-pdf-cols-mig1';  // one-time inject of columns added after a user's first save
  const [open, setOpen] = useState(false);
  const [cols, setCols] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(LSK));
      if (Array.isArray(s) && s.length) {
        if (!localStorage.getItem(MIGK)) {  // add new P/Hr columns once for existing saved selections
          const merged = [...s];
          ['pphRaw', 'pphOnb'].forEach(k => { if (!merged.includes(k)) merged.push(k); });
          try { localStorage.setItem(LSK, JSON.stringify(merged)); localStorage.setItem(MIGK, '1'); } catch {}
          return merged;
        }
        return s;
      }
      return DEFAULT_PDF_COLS;
    } catch { return DEFAULT_PDF_COLS; }
  });
  const toggle = k => setCols(c => c.includes(k) ? c.filter(x => x !== k) : [...c, k]);
  const download = () => {
    try { localStorage.setItem(LSK, JSON.stringify(cols)); } catch {}
    onDownload(cols);
    setOpen(false);
  };
  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-primary btn-sm" onClick={() => setOpen(o => !o)}>
        <Download size={13} /> Download PDF
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 100, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14, width: 240, boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
          <div style={{ fontWeight: 700, fontSize: '.8rem', marginBottom: 4 }}>Columns to include</div>
          <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginBottom: 10 }}>Date is always included.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto', marginBottom: 12 }}>
            {PDF_COLS.map(c => (
              <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.78rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={cols.includes(c.key)} onChange={() => toggle(c.key)} />
                {c.label}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={download} disabled={cols.length === 0}>Download</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RenameDriverBtn({ allDrivers, onRename }) {
  const [open, setOpen]   = useState(false);
  const [from, setFrom]   = useState('');
  const [to, setTo]       = useState('');

  const apply = () => {
    const f = from.trim().toUpperCase();
    const t = to.trim().toUpperCase();
    if (!f || !t || f === t) return;
    if (onRename(f, t)) { setOpen(false); setFrom(''); setTo(''); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-secondary btn-sm" onClick={() => setOpen(o => !o)}>
        Rename Driver
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 100, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 16, width: 280, boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
          <div style={{ fontWeight: 700, fontSize: '.8rem', marginBottom: 12 }}>Rename Driver Code</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <select className="form-select" value={from} onChange={e => setFrom(e.target.value)}>
              <option value="">Select driver to rename…</option>
              {allDrivers.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <input className="form-input" placeholder="New code (e.g. SHARLANDS2)"
              value={to} onChange={e => setTo(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && apply()} />
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={apply} disabled={!from || !to}>Apply</button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setOpen(false); setFrom(''); setTo(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SetStartTimeBtn ───────────────────────────────────────────────────────────
function SetStartTimeBtn({ allDrivers, onSet }) {
  const [open, setOpen] = useState(false);
  const [drv, setDrv]   = useState('');
  const [time, setTime] = useState('06:30');
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!drv || !time) return;
    setBusy(true);
    const ok = await onSet(drv, time);
    setBusy(false);
    if (ok) { setOpen(false); setDrv(''); }
  };
  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-secondary btn-sm" onClick={() => setOpen(o => !o)}>Set Start Time</button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 100, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 16, width: 300, boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
          <div style={{ fontWeight: 700, fontSize: '.8rem', marginBottom: 4 }}>Set Driver Start Time</div>
          <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginBottom: 12, lineHeight: 1.4 }}>Updates the Drivers tab and recomputes corrected hours across all periods → flows to Activity Summary, Hourly Drivers, GCCD Report and Uplift Workbench.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <select className="form-select" value={drv} onChange={e => setDrv(e.target.value)}>
              <option value="">Select driver…</option>
              {allDrivers.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <input className="form-input" type="time" value={time} onChange={e => setTime(e.target.value)} onKeyDown={e => e.key === 'Enter' && apply()} />
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={apply} disabled={!drv || !time || busy}>{busy ? 'Saving…' : 'Apply to all pages'}</button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setOpen(false); setDrv(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── AnalysisTab ───────────────────────────────────────────────────────────────
function AnalysisTab({ periodRows, allDrivers, selectedPeriod, breakdownBucket, setBreakdownBucket,
  breakdownDriver, setBreakdownDriver, sheet1Totals, reconcLoading, onRefreshReconc }) {

  const [dailySort, setDailySort] = useState('date'); // 'date'|'onboard'|'pph'|'delivered'

  const dp  = (n, d=1) => n != null && !isNaN(n) ? Number(n).toFixed(d) : '—';
  const num = n => n != null && n !== '' ? Number(n).toLocaleString() : '—';
  const hrs = n => n != null ? Number(n).toFixed(2) + 'h' : '—';

  // ── Time bucketing helpers ──────────────────────────────────────────────────
  function weekLabel(iso) {
    const [y,m,d] = iso.split('-').map(Number);
    const dt = new Date(y,m-1,d);
    const day = dt.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const mon = new Date(dt); mon.setDate(dt.getDate() + diff);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    const fmt = d2 => d2.toLocaleDateString('en-AU', { day:'numeric', month:'short' });
    return `${fmt(mon)} – ${fmt(sun)}`;
  }
  function monthLabel(iso) {
    const [y,m] = iso.split('-').map(Number);
    return new Date(y,m-1,1).toLocaleDateString('en-AU', { month:'long', year:'numeric' });
  }
  function bucketKey(iso) {
    if (breakdownBucket === 'day')   return iso;
    if (breakdownBucket === 'week')  return weekLabel(iso);
    if (breakdownBucket === 'month') return iso.slice(0,7);
    return iso;
  }
  function bucketLabel(key) {
    if (breakdownBucket === 'month') {
      const [y,m] = key.split('-').map(Number);
      return new Date(y,m-1,1).toLocaleDateString('en-AU', { month:'long', year:'numeric' });
    }
    return key;
  }

  // Build breakdown buckets
  const srcRows = breakdownDriver
    ? periodRows.filter(r => r.login === breakdownDriver)
    : periodRows;

  const buckets = {};
  srcRows.forEach(r => {
    const k = bucketKey(r.date);
    if (!buckets[k]) buckets[k] = { delivered:0, onboard:0, carded:0, hours:0, rawHours:0, days:0 };
    buckets[k].delivered += r.deliveredItems  || 0;
    buckets[k].onboard   += r.onboardItems    || 0;
    buckets[k].carded    += r.cardedItems     || 0;
    buckets[k].hours     += r.correctedHours  || 0;
    buckets[k].rawHours  += r.rawHours        || 0;
    buckets[k].days      += 1;
  });

  const bucketKeys = Object.keys(buckets).sort();

  // Fleet PPH sorted for ranking
  const pphRanking = allDrivers.map(login => {
    const dRows = periodRows.filter(r => r.login === login);
    const del  = dRows.reduce((s,r) => s + (r.deliveredItems || 0), 0);
    const hrs2 = dRows.reduce((s,r) => s + (r.correctedHours || 0), 0);
    return { login, del, hrs: hrs2, pph: hrs2 > 0 ? del/hrs2 : null };
  }).filter(x => x.pph != null).sort((a,b) => b.pph - a.pph);

  // ── Reconciliation ─────────────────────────────────────────────────────────
  const reconcRows = allDrivers.map(login => {
    const waveRows = periodRows.filter(r => r.login === login);
    const waveDelivered = waveRows.reduce((s,r) => s + (r.deliveredItems || 0), 0);
    const waveOnboard   = waveRows.reduce((s,r) => s + (r.onboardItems   || 0), 0);
    const s1 = sheet1Totals?.byDriver?.[login];
    const s1Total = s1 ? Math.round(s1.total) : null;
    const diff = s1Total != null ? waveOnboard - s1Total : null;
    const pct  = s1Total != null && s1Total > 0 ? (diff / s1Total) * 100 : null;
    return { login, waveDelivered, waveOnboard, s1Total, diff, pct };
  }).filter(r => r.waveOnboard > 0 || r.s1Total != null);

  const thS = { padding: '9px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '.06em', color: '#6b7280', background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', textAlign: 'left' };
  const thR = { ...thS, textAlign: 'right' };

  const varianceColor = pct => {
    if (pct == null) return 'var(--hp-dim)';
    const abs = Math.abs(pct);
    return abs <= 5 ? 'var(--hp-green)' : abs <= 15 ? 'var(--hp-amber)' : 'var(--hp-red)';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── PPH Ranking ── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--hp-border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <BarChart2 size={16} color="var(--hp-gold)" />
          <div style={{ fontWeight: 700, fontSize: '.9rem' }}>Parcels Per Hour — Driver Ranking (Engine / Legal)</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ minWidth: 600 }}>
            <thead><tr>
              <th style={thS}>#</th>
              <th style={thS}>Driver</th>
              <th style={thR}>Delivered</th>
              <th style={thR}>Corr. Hours</th>
              <th style={thR}>P/Hr (Engine)</th>
              <th style={thR}>P/Hr (Raw)</th>
              <th style={thR}>Variance</th>
            </tr></thead>
            <tbody>
              {pphRanking.map((r, i) => {
                const rawPPH = r.hrs > 0 ? r.del / (periodRows.filter(x => x.login === r.login).reduce((s,x) => s + (x.rawHours||0), 0) || 1) : null;
                const variance = rawPPH != null ? rawPPH - r.pph : null;
                const color = r.pph >= 20 ? 'var(--hp-green)' : r.pph >= 15 ? 'var(--hp-amber)' : 'var(--hp-red)';
                return (
                  <tr key={r.login}>
                    <td style={{ fontSize: '.8rem', color: 'var(--hp-dim)', fontWeight: 700 }}>#{i+1}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: '.8rem', fontWeight: 600 }}>{r.login}</td>
                    <td style={{ textAlign: 'right', fontSize: '.8rem', fontWeight: 600, color: 'var(--hp-green)' }}>{num(r.del)}</td>
                    <td style={{ textAlign: 'right', fontSize: '.8rem' }}>{hrs(r.hrs)}</td>
                    <td style={{ textAlign: 'right', fontSize: '.85rem', fontWeight: 800, color }}>{dp(r.pph)}</td>
                    <td style={{ textAlign: 'right', fontSize: '.8rem', color: '#6366f1' }}>{dp(rawPPH)}</td>
                    <td style={{ textAlign: 'right', fontSize: '.8rem', color: variance != null && variance > 0 ? 'var(--hp-amber)' : 'var(--hp-dim)' }}>
                      {variance != null ? (variance > 0 ? '+' : '') + dp(variance) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '8px 16px', fontSize: '.72rem', color: 'var(--hp-dim)', borderTop: '1px solid var(--hp-border)' }}>
          Green ≥ 20 · Amber 15–20 · Red &lt; 15 · Variance = Raw P/Hr minus Engine P/Hr (AP overstates by this amount)
        </div>
      </div>

      {/* ── Fleet Daily PPH ── */}
      {(() => {
        // Build hours from Wave CSV, parcels from Sheet1 (source of truth)
        const byDay = {};
        periodRows.forEach(r => {
          if (!byDay[r.date]) byDay[r.date] = { date: r.date, drivers: new Set(), hours: 0 };
          byDay[r.date].drivers.add(r.login);
          byDay[r.date].hours += r.correctedHours || 0;
        });

        // Overlay Sheet1 parcel totals per day (sum all drivers for that date)
        const s1ByDate = sheet1Totals?.byDate || {};
        const allDates = new Set([...Object.keys(byDay), ...Object.keys(s1ByDate)]);

        const days = [...allDates].map(date => {
          const waveDay = byDay[date] || { drivers: new Set(), hours: 0 };
          const s1Day   = s1ByDate[date] || {};
          const s1Total = Object.values(s1Day).reduce((s, d) => s + (d.total || 0), 0);
          const parcels = sheet1Totals ? Math.round(s1Total) : null; // null until sheet1 loaded
          return {
            date,
            driverCount: waveDay.drivers.size,
            hours:   waveDay.hours,
            parcels: parcels,
            pph: parcels != null && waveDay.hours > 0
              ? Math.round((parcels / waveDay.hours) * 10) / 10 : null,
          };
        }).filter(d => d.driverCount > 0 || (d.parcels != null && d.parcels > 0));

        const sorted = [...days].sort((a, b) => {
          if (dailySort === 'date')      return a.date.localeCompare(b.date);
          if (dailySort === 'delivered') return (b.parcels || 0) - (a.parcels || 0);
          if (dailySort === 'pph')       return (b.pph || 0) - (a.pph || 0);
          return a.date.localeCompare(b.date);
        });

        const pphVals  = days.filter(d => d.pph != null).map(d => d.pph);
        const pphMax   = Math.max(...pphVals) || 1;
        const maxParcels = Math.max(...days.map(d => d.parcels || 0)) || 1;

        const pphColor = pph => {
          if (pph == null) return 'var(--hp-dim)';
          const t = pph / pphMax;
          return t >= 0.8 ? 'var(--hp-green)' : t >= 0.6 ? 'var(--hp-amber)' : 'var(--hp-red)';
        };

        const fmtDate = iso => {
          const [y,m,d2] = iso.split('-').map(Number);
          return new Date(y,m-1,d2).toLocaleDateString('en-AU', { weekday:'long', day:'numeric', month:'long' });
        };

        const totParcels = sheet1Totals ? days.reduce((s,d) => s + (d.parcels || 0), 0) : null;
        const totHours   = Math.round(days.reduce((s,d) => s + d.hours, 0) * 100) / 100;
        const totPPH     = totParcels != null && totHours > 0
          ? Math.round((totParcels / totHours) * 10) / 10 : null;

        const SortBtn = ({ id, label }) => (
          <button onClick={() => setDailySort(id)}
            className={`btn btn-sm ${dailySort === id ? 'btn-primary' : 'btn-secondary'}`}>
            {label}
          </button>
        );

        return (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--hp-border)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '.95rem' }}>Fleet Daily — Parcels Per Hour</div>
                <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>
                  Parcels from Sheet1 · Hours from Wave CSV
                  {!sheet1Totals && <span style={{ color: 'var(--hp-amber)', marginLeft: 8 }}>↑ Click Refresh on Reconciliation to load parcel data</span>}
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
                <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginRight: 4 }}>Sort:</span>
                <SortBtn id="date"      label="Date" />
                <SortBtn id="delivered" label="Busiest First" />
                <SortBtn id="pph"       label="Highest PPH" />
              </div>
            </div>

            {/* Day rows */}
            <div style={{ padding: '8px 0' }}>
              {sorted.map((d, i) => (
                <div key={d.date} style={{
                  display: 'grid', gridTemplateColumns: '260px 1fr 120px 120px',
                  alignItems: 'center', gap: 0,
                  padding: '10px 20px',
                  borderBottom: '1px solid var(--hp-border)',
                  background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
                }}>
                  {/* Date */}
                  <div style={{ fontWeight: 700, fontSize: '.88rem', color: 'var(--hp-text)' }}>
                    {fmtDate(d.date)}
                    <span style={{ marginLeft: 8, fontSize: '.7rem', color: 'var(--hp-dim)', fontWeight: 500 }}>
                      {d.driverCount} drivers
                    </span>
                  </div>

                  {/* Volume bar */}
                  <div style={{ paddingRight: 24 }}>
                    {d.parcels != null ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ flex: 1, height: 10, background: 'var(--hp-border)', borderRadius: 5, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: 5,
                            width: `${Math.round((d.parcels / maxParcels) * 100)}%`,
                            background: 'var(--hp-blue)', transition: 'width .3s',
                          }} />
                        </div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.88rem', fontWeight: 700, minWidth: 55, textAlign: 'right' }}>
                          {d.parcels.toLocaleString()}
                          <span style={{ fontSize: '.65rem', color: 'var(--hp-dim)', fontWeight: 400, marginLeft: 3 }}>parcels</span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', fontStyle: 'italic' }}>
                        Refresh reconciliation to load Sheet1 data
                      </div>
                    )}
                  </div>

                  {/* Hours */}
                  <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '.85rem', color: 'var(--hp-dim)' }}>
                    {d.hours.toFixed(1)}h
                  </div>

                  {/* PPH */}
                  <div style={{ textAlign: 'right' }}>
                    <span style={{
                      fontFamily: 'var(--font-mono)', fontSize: '1.1rem', fontWeight: 800,
                      color: pphColor(d.pph),
                    }}>
                      {d.pph != null ? d.pph : '—'}
                    </span>
                    <span style={{ fontSize: '.68rem', color: 'var(--hp-dim)', marginLeft: 4 }}>p/hr</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Period total footer */}
            <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr 120px 120px', alignItems: 'center', gap: 0, padding: '12px 20px', background: 'var(--hp-surface)', borderTop: '2px solid var(--hp-gold)' }}>
              <div style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--hp-gold)' }}>
                PERIOD TOTAL — {days.length} days
              </div>
              <div style={{ paddingRight: 24, textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '.88rem' }}>
                {totParcels != null ? totParcels.toLocaleString() : '—'}
                <span style={{ fontSize: '.65rem', color: 'var(--hp-dim)', fontWeight: 400, marginLeft: 3 }}>parcels</span>
              </div>
              <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '.85rem', color: 'var(--hp-dim)', fontWeight: 700 }}>
                {totHours}h
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.1rem', fontWeight: 800, color: 'var(--hp-gold)' }}>
                  {totPPH != null ? totPPH : '—'}
                </span>
                <span style={{ fontSize: '.68rem', color: 'var(--hp-dim)', marginLeft: 4 }}>p/hr</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Time Breakdown ── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--hp-border)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, fontSize: '.9rem', marginRight: 8 }}>Time Breakdown</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {['day','week','month'].map(b => (
              <button key={b} onClick={() => setBreakdownBucket(b)}
                className={`btn btn-sm ${breakdownBucket === b ? 'btn-primary' : 'btn-secondary'}`}>
                {b.charAt(0).toUpperCase() + b.slice(1)}
              </button>
            ))}
          </div>
          <select className="form-select" style={{ marginLeft: 'auto', minWidth: 160 }}
            value={breakdownDriver} onChange={e => setBreakdownDriver(e.target.value)}>
            <option value="">All drivers</option>
            {allDrivers.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead><tr>
              <th style={thS}>{breakdownBucket.charAt(0).toUpperCase() + breakdownBucket.slice(1)}</th>
              <th style={thR}>Days</th>
              <th style={thR}>Onboard</th>
              <th style={thR}>Delivered</th>
              <th style={thR}>Del %</th>
              <th style={thR}>Carded</th>
              <th style={thR}>Crd %</th>
              <th style={thR}>Corr. Hours</th>
              <th style={thR}>P/Hr</th>
            </tr></thead>
            <tbody>
              {bucketKeys.map(k => {
                const b   = buckets[k];
                const pph = b.hours > 0 ? b.delivered / b.hours : null;
                const delPct = b.onboard > 0 ? (b.delivered / b.onboard) * 100 : null;
                const crdPct = b.onboard > 0 ? (b.carded    / b.onboard) * 100 : null;
                return (
                  <tr key={k}>
                    <td style={{ fontSize: '.8rem', fontWeight: 600 }}>{bucketLabel(k)}</td>
                    <td style={{ textAlign: 'right', fontSize: '.8rem', color: 'var(--hp-dim)' }}>{b.days}</td>
                    <td style={{ textAlign: 'right', fontSize: '.8rem' }}>{num(b.onboard)}</td>
                    <td style={{ textAlign: 'right', fontSize: '.8rem', fontWeight: 600, color: 'var(--hp-green)' }}>{num(b.delivered)}</td>
                    <td style={{ textAlign: 'right', fontSize: '.8rem', color: delPct != null && delPct < 85 ? 'var(--hp-amber)' : 'var(--hp-dim)' }}>{dp(delPct)}%</td>
                    <td style={{ textAlign: 'right', fontSize: '.8rem' }}>{num(b.carded)}</td>
                    <td style={{ textAlign: 'right', fontSize: '.8rem', color: crdPct != null && crdPct > 15 ? 'var(--hp-amber)' : 'var(--hp-dim)' }}>{dp(crdPct)}%</td>
                    <td style={{ textAlign: 'right', fontSize: '.8rem' }}>{hrs(b.hours)}</td>
                    <td style={{ textAlign: 'right', fontSize: '.85rem', fontWeight: 700, color: pph == null ? 'var(--hp-dim)' : pph >= 20 ? 'var(--hp-green)' : pph >= 15 ? 'var(--hp-amber)' : 'var(--hp-red)' }}>{dp(pph)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Sheet1 Reconciliation ── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--hp-border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={16} color="var(--hp-amber)" />
          <div style={{ fontWeight: 700, fontSize: '.9rem' }}>Wave CSV vs Sheet1 Reconciliation</div>
          <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginLeft: 4 }}>Wave delivered vs Sheet1 source-of-truth</div>
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}
            onClick={onRefreshReconc} disabled={reconcLoading}>
            <RefreshCw size={12} /> {reconcLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {reconcLoading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--hp-dim)', fontSize: '.875rem' }}>
            <div style={{ width: 32, height: 32, border: '3px solid var(--hp-border)', borderTopColor: 'var(--hp-gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
            Loading Sheet1 data…
          </div>
        ) : !sheet1Totals ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--hp-dim)', fontSize: '.875rem' }}>
            Click Refresh to load Sheet1 totals for reconciliation
          </div>
        ) : (
          <>
            {/* Summary banner */}
            {(() => {
              const matched  = reconcRows.filter(r => r.pct != null && Math.abs(r.pct) <= 5).length;
              const warnings = reconcRows.filter(r => r.pct != null && Math.abs(r.pct) > 5 && Math.abs(r.pct) <= 15).length;
              const alerts   = reconcRows.filter(r => r.pct != null && Math.abs(r.pct) > 15).length;
              const noSheet1 = reconcRows.filter(r => r.s1Total == null).length;
              return (
                <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--hp-border)' }}>
                  {[
                    { label: 'Matched (≤5%)',    value: matched,  color: 'var(--hp-green)' },
                    { label: 'Warning (5–15%)',   value: warnings, color: 'var(--hp-amber)' },
                    { label: 'Alert (>15%)',      value: alerts,   color: 'var(--hp-red)'   },
                    { label: 'No Sheet1 data',    value: noSheet1, color: 'var(--hp-dim)'   },
                  ].map(s => (
                    <div key={s.label} style={{ flex: 1, padding: '12px 16px', borderRight: '1px solid var(--hp-border)', textAlign: 'center' }}>
                      <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.07em', fontWeight: 700 }}>{s.label}</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: s.color, marginTop: 4 }}>{s.value}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead><tr>
                  <th style={thS}>Driver</th>
                  <th style={thR}>Wave Onboard</th>
                  <th style={thR}>Wave Delivered</th>  {/* kept for reference */}
                  <th style={thR}>Sheet1 Total</th>
                  <th style={thR}>Difference</th>
                  <th style={thR}>Variance %</th>
                  <th style={thS}>Status</th>
                </tr></thead>
                <tbody>
                  {reconcRows.sort((a,b) => (b.pct != null ? Math.abs(b.pct) : -1) - (a.pct != null ? Math.abs(a.pct) : -1)).map(r => {
                    const vc = varianceColor(r.pct);
                    const status = r.s1Total == null ? 'No Sheet1 data'
                      : Math.abs(r.pct) <= 5 ? '✓ Matched'
                      : Math.abs(r.pct) <= 15 ? '⚠ Warning'
                      : '✕ Alert';
                    return (
                      <tr key={r.login}>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: '.8rem', fontWeight: 600 }}>{r.login}</td>
                        <td style={{ textAlign: 'right', fontSize: '.8rem', fontWeight: 600, color: 'var(--hp-blue)' }}>{num(r.waveOnboard)}</td>
                        <td style={{ textAlign: 'right', fontSize: '.8rem', color: 'var(--hp-dim)' }}>{num(r.waveDelivered)}</td>
                        <td style={{ textAlign: 'right', fontSize: '.8rem', color: 'var(--hp-gold)' }}>{r.s1Total != null ? num(r.s1Total) : '—'}</td>
                        <td style={{ textAlign: 'right', fontSize: '.8rem', fontWeight: 600, color: vc }}>
                          {r.diff != null ? (r.diff > 0 ? '+' : '') + num(r.diff) : '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontSize: '.8rem', fontWeight: 700, color: vc }}>
                          {r.pct != null ? (r.pct > 0 ? '+' : '') + dp(r.pct) + '%' : '—'}
                        </td>
                        <td style={{ fontSize: '.78rem', fontWeight: 600, color: vc }}>{status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '8px 16px', fontSize: '.72rem', color: 'var(--hp-dim)', borderTop: '1px solid var(--hp-border)' }}>
              Difference = Wave Onboard − Sheet1 Total · Positive = Wave shows more · Negative = Sheet1 shows more
            </div>
          </>
        )}
      </div>
    </div>
  );
}
